/**
 * voice-v8 — PTT-Aufnahmekarte (Neufassung 2026-07-22, Martins Auftrag:
 * "komplett ueberarbeiten, gleiches Erscheinungsbild — auch beim Fixieren").
 *
 * EINE Karte, zwei Zustaende:
 *   Halten:     Kopf (Puls-Punkt + Status | Timer) + Wellenform +
 *               Gesten-Hinweis ("Loslassen sendet · hochziehen verriegelt …").
 *   Verriegelt: gleiche Karte, gleicher Kopf, gleiche Welle — statt des
 *               Hinweises erscheinen die Aktions-Knoepfe (Verwerfen/Senden).
 * Die Karte sitzt bewusst OBEN (2026-07-06: unten verdeckte sie der Daumen).
 *
 * API unveraendert (openPttModal/setStatus/setLocked/setSpeaking/close) —
 * app.ts bleibt unangetastet. Pure DOM, best-effort, keine throws.
 *
 * Dritter Zustand „Sende …“ (30.09.2026, Ticket 2634): statt der toten
 * Knopfreihe der „Ausweg“ — ein Knopf „Ausblenden“ (und Escape), der die Karte
 * schließt, ohne den Zug abzubrechen. Dazu `onAusblenden` in den Rückrufen.
 *
 * Neu geordnet (01.10.2026, pre.3551): die beiden Bedienknöpfe (Stumm, Auto-Mikro)
 * stehen in EINER Gruppe `.pttm-tools`; die Statuszeile zeigt für die „Anzeige
 * hängt“-Sätze eine Kurzfassung (`hinweisKurz`), für Vorleser bleibt der ganze Satz;
 * die Mitschrift meldet `.ueberlaeuft`, damit sie oben sanft ausläuft. Aussehen,
 * Platz und Raster: ein Block in style.css („DIE AUFNAHMEKARTE“).
 */

import { createMicViz, PEGEL_LUECKE_MS } from "./mic-visualizer.js";
import { taktZusatz } from "./rechentakt.js";
import { schreibeScrollTop } from "./scroll-ruhe.js";
import {
  DAUERLAERM_PEGEL,
  endwortAmEnde,
  neuerTextVerlauf,
  sendeGrund,
  stilleWeg,
  type LiveWort,
  type SendeEinstellung,
  type SendeGrund,
  type SendeLage,
} from "./jetzt-senden.js";
import { meldeMikroAnlass, meldeSpur } from "./mikro-spur.js";
import { brauchtEinstellungen, stummHinweisFuer, type MikroRecht } from "./mikro-recht.js";
import { merkeBild, neueStilleMessung, stilleZeile } from "./stille-messung.js";
import { neuerPegelVerlauf, rauschBefund } from "./rausch-woerter.js";
import { ohneEndWoerter, type RauschMessung } from "../../shared/rausch-woerter.js";
import {
  endeZeile,
  merkeAutoSenden,
  merkeEnde,
  merkeLive,
  merkePegel,
  merkeTextNeu,
  merkeTonOhneWort,
  merkeWortzahl,
  neueEndeMessung,
  pauseWeiterZeile,
  type EndeSignal,
} from "./ende-token.js";
import { playBeep } from "./audio-feedback.js";
import { wennRecBereit } from "./rec-bereit.js";
import {
  hoereAufLeitungsWechsel,
  hoereAufStummeQuelle,
  PTT_MAX_MS,
  PTT_TEIL_HINWEIS_VOR_MS,
  PTT_WARNUNG_VOR_MS,
  type TeilLage,
} from "./ptt.js";
import type { AutoMikroStufe } from "./settings.js";
import {
  browserSpeicher,
  hinweisSchonGezeigt,
  istIosWeb,
  IOS_DIKTAT_HINWEIS,
  LANGES_DIKTAT_MS,
  merkeHinweisGezeigt,
  sollIosHinweisZeigen,
} from "./ios-diktat-hinweis.js";
import {
  AUTO_FRAGE_AB_MS,
  AUTO_FRAGE_FRUEHESTENS_MS,
  autoPruefung,
  autoRestMs,
  autoSendeAbMs,
  jevUrteilZeile,
  nurSatzendeNeu,
  satzEnde,
  STILLE_AUTO_MS,
  stilleKurz,
  tempoLesen,
  TEXTRUHE_AB_MS,
  zusageAntwort,
  urteilEnde,
  type AutoAntwort,
  type AutoFolge,
  type AutoLage,
  type AutoUrteil,
} from "../../shared/stille-auto.js";
import { jevMoeglich } from "./jev-konto.js";
import { bremsWarteHinweis, neueBremse, type SatzOffen } from "../../shared/satz-vollstaendig.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * 28.10.2026 (Martin: „das Fenster ist länger offen als nötig“): so lange blendet die Karte aus, bevor sie aus dem DOM geht.
 * Muss zur Dauer von `ptt-card-out` / `ptt-fokus-aus` in style.css passen (90 ms) — dort 10 ms weniger, damit nichts abgeschnitten
 * wird. Vorher 150 ms.
 */
export const AUSBLEND_MS = 100;

/**
 * 12.10.2026 (Feinschliff): hat der Server mit seiner LETZTEN Antwort `fein: true` gemeldet (Schalter
 * `STILLE_FEIN`)? Gilt für die ganze Seite, nicht nur für eine Aufnahme: sonst fragte die erste Pause jeder
 * Aufnahme noch bei 1,0 s. Vor der ersten Antwort nach dem Laden der Seite und nach `fein: false` gilt das
 * Alte (1,0 s). Ein Ausfall (keine Antwort) ändert nichts daran.
 */
let feinZuletzt = false;
/** Nur für Tests: vergisst, was der Server zuletzt zum Feinschliff gesagt hat. */
export function _feinZuruecksetzen(): void {
  feinZuletzt = false;
}
/**
 * 12.10.2026 (Bahn „fortsetzung-anhaengen“): hat der Server mit seiner LETZTEN Antwort `fortsetzung: true` gemeldet (Schalter `FORTSETZUNG`)?
 * Wie `feinZuletzt` für die ganze Seite — die Fortsetzung beginnt nach dem Schließen dieser Karte, in der nächsten. Vor der ersten Antwort nach
 * dem Laden der Seite und nach `fortsetzung: false`/ohne Angabe: nein (im Zweifel das alte Verhalten). Ein Ausfall ändert nichts daran.
 */
let fortsetzungZuletzt = false;
export function fortsetzungErlaubt(): boolean {
  return fortsetzungZuletzt;
}
/** Nur für Tests. */
export function _fortsetzungZuruecksetzen(): void {
  fortsetzungZuletzt = false;
}
/**
 * 15.10.2026 (Bahn „stille-zusage“): hat der Server mit seiner LETZTEN Antwort `zusage: true` gemeldet (Schalter `STILLE_ZUSAGE`)? Wie
 * `feinZuletzt` für die ganze Seite: so geht eine kurze Zusage („Okay, und weiter“, shared/stille-auto.ts `kurzeZusage`) nach 1,2 s hinaus,
 * auch wenn Jevs Urteil für genau diesen Text noch unterwegs ist. Vor der ersten Antwort gilt die Regel nur mit dem Urteil selbst (`undefined`; nach einer Frage geht ein Ja trotzdem sofort, solange der Server nicht `false` sagte).
 */
let zusageZuletzt: boolean | undefined;
/** Nur für Tests. */
export function _zusageZuruecksetzen(): void {
  zusageZuletzt = undefined;
}
/**
 * 17.10.2026: die Zeile für den Hinweis, wenn eine kurze Zusage nach einer Frage sofort hinausgeht — fester Text, ohne Inhalt der
 * Nachricht. Die Karte zeigt ihn in der Statuszeile (`role="status"`), der Ton und die Vibration kommen von der Quittung (app.ts).
 */
export const ZUSAGE_GESENDET_HINWEIS: Readonly<Record<"Ja" | "Nein" | "neutral", string>> = {
  Ja: "Ja gesendet",
  Nein: "Nein gesendet",
  neutral: "Gesendet",
};

/**
 * 15.10.2026 (Bahn „wortstille“): hat der Server mit seiner LETZTEN Antwort `wortruhe: true` gemeldet (Schalter `STILLE_WORTRUHE`)? Wie
 * `zusageZuletzt`: so fragt die Karte Jev auch dann, wenn der Pegel nach dem letzten Wort nie still wird (shared/stille-auto.ts,
 * `autoWortStille`). Mit Urteil für genau diesen Text zählt dessen `wortruhe`.
 */
let wortruheZuletzt = false;
/** Nur für Tests. */
export function _wortruheZuruecksetzen(): void {
  wortruheZuletzt = false;
}

/** Ticket 2618: die Sätze der Karte zu den Teilen — die ersten beiden ruhig, kein Alarm. */
export const teilKommtSatz = (nr: number): string => `Gleich beginnt Teil ${nr} – einfach weitersprechen.`;
export const teilLaeuftSatz = (nr: number): string => `Teil ${nr} läuft – alles kommt in eine Nachricht.`;
/**
 * Ticket 2618 (Prüfbefund 29.09.): Teil `nr` kam 30 s vor der Höchstdauer
 * noch nicht zustande — dann ist es wieder die Warnung vor dem Ende, mit
 * Ton und Amber. Ein letzter Versuch folgt noch; gelingt er, sagt die
 * Zeile „Teil … läuft".
 */
export const teilHaktSatz = (nr: number): string =>
  `Teil ${nr} startet nicht – in 30 Sekunden endet die Aufnahme.`;

/**
 * Nach so langer völliger Stille bricht die Sprechtaste von selbst ab.
 * (25.09.2026, Martin: „Wenn Push-to-Talk geklickt wird und 10 Sekunden
 * kein einziges Geräusch kommt, soll er die Verbindung kappen — falls man
 * unabsichtlich draufdrückt.")
 */
export const LEER_ABBRUCH_MS = 10_000;

/**
 * Abbrechen, weil nichts kam? Nur, wenn die Aufnahme wirklich läuft, seit
 * LEER_ABBRUCH_MS nichts zu hören war (Pegel, Mitschrift) UND auch die echte
 * Aufnahme keinen Ton hat — ein leises Mikro ist kein leerer Druck.
 */
export function leerAbbrechen(z: { laeuftSeitMs: number | null; gesprochen: boolean; echterTon: boolean }): boolean {
  return z.laeuftSeitMs !== null && z.laeuftSeitMs >= LEER_ABBRUCH_MS && !z.gesprochen && !z.echterTon;
}

/**
 * Die Tasche: länger verdeckt als das, und die Aufnahme wird verworfen.
 * (25.09.2026)
 *
 * Martin: *„Schon wieder so ein scheiß Pocket Call."* Zweimal an einem Tag:
 * 14:18 (Sprechtaste gedrückt, im selben Moment Bildschirm aus, 15 min bis
 * zur Höchstdauer) und 15:54 („Hey Vespera", 8 s später Bildschirm aus,
 * 7 min). Verdeckt misst die Seite keinen Pegel — weder der Leer-Abbruch
 * noch das Absenden nach Stille können dort greifen. Beim Entsperren ging
 * alles raus, samt einem Gespräch mit Dritten.
 *
 * Verworfen, nicht gesendet: was in der Tasche aufgenommen wurde, ist nicht
 * an Vespera gerichtet. Gemessen seit 18.09.: die Hälfte der Nachrichten
 * ist unter 24 s, neun von zehn unter 91 s — und die entstehen vor
 * offenem Bildschirm.
 */
export const VERDECKT_VERWURF_MS = 60_000;

/**
 * Schutzfenster gegen Geister-Klicks bei einem Fenster, das die App SELBST geöffnet hat (`via=nach-frage`). (19.10.2026)
 *
 * Martin 06:53: „Es stand ein Modal da mit einer Frage … dann kam ein Knopf, wo ich antworten hätte können, aber dann war der Bezug
 * zu dieser Frage nicht mehr hergestellt.“ Journal: `via=nach-frage` → nach 1108 ms `abbruch … grund=fenster-verwerfen`. Das Fenster
 * deckt den Chat ab, in dem eben noch die Knöpfe der Frage standen; wer dort tippt, trifft den Hintergrund oder (nach einer
 * Verschiebung beim Einblenden der Ja/Nein-Knöpfe) „Verwerfen“ — die Frage war dann ohne Antwort verworfen. Der Grund stand nicht
 * im Protokoll, weil `onCancel()` ohne Angabe lief.
 *
 * Ein Druck auf „Verwerfen“ zählt bei einem von selbst geöffneten Fenster erst nach dieser Frist; der Hintergrund verwirft bei
 * `nachFrage` nie (auch nicht danach). Echte Absicht bleibt über den Knopf möglich; Escape und alle Wege der Karte selbst
 * (Stille, Tasche) sind davon nicht betroffen.
 */
export const GEISTERKLICK_SCHUTZ_MS = 1500;

/** Zählt ein Druck auf „Verwerfen“/Hintergrund? Nur bei einem von selbst geöffneten Fenster gilt die Schutzfrist. */
export function verwerfenDruckZaehlt(z: { vonSelbst: boolean; offenSeitMs: number }): boolean {
  return !z.vonSelbst || z.offenSeitMs >= GEISTERKLICK_SCHUTZ_MS;
}

function verdecktVerwerfen(z: { verdecktSeitMs: number | null }): boolean {
  return z.verdecktSeitMs !== null && z.verdecktSeitMs >= VERDECKT_VERWURF_MS;
}

/**
 * Das Nachlausch-Fenster. (26.09.2026, Ticket 2668)
 *
 * Martin, auf der Autobahn: *„Nach JEDER gesprochenen Antwort bleibt das
 * Mikro noch etwa 2–3 Sekunden lauschend offen. … Bleibt es still, schließt
 * sich das Mikro wieder."* Geht das Mikro von selbst auf (nach einer Frage
 * oder nach jeder Antwort) und kommt in `fensterMs` keine Sprache, schließt
 * es ohne zu senden.
 *
 * ⚠️ Die Frist zählt ab dem WIRKLICH offenen Mikro (Aufnahmestart), nicht
 *    ab dem Ende der Wiedergabe: am Bluetooth im Auto dauert das Öffnen
 *    1,5 bis 7 s. Und sie braucht einen Pegel, der das ganze Fenster über
 *    ohne Lücke gemessen hat (`gemessenMs`): wer nicht misst, weiß nicht,
 *    ob gesprochen wurde.
 * ⚠️ Steht der Pegel (`gemessenMs` null), bleibt es offen — dann hört das
 *    Nachhören (nachhoeren.ts) mit, und „Senden" oder die Stille schicken ab.
 */
export function nachlauschenZu(z: {
  /** Die Länge des Fensters; 0 = aus. */
  fensterMs: number;
  /** Seit dem Aufnahmestart; null = die Aufnahme läuft noch nicht. */
  offenSeitMs: number | null;
  gesprochen: boolean;
  /** So lange misst der Pegel schon ohne Lücke; null = er misst gerade nicht. */
  gemessenMs: number | null;
  /**
   * Läuft gerade ein lautes Stück, das (noch) nicht als Sprache zählt?
   * Dann wartet es: ein Satz, der kurz vor dem Ende beginnt, braucht
   * TON_ANHALTEND_MS, bis er zählt. (29.09.2026, Ticket 2668)
   */
  tonLaeuft?: boolean;
}): boolean {
  if (z.fensterMs <= 0 || z.offenSeitMs === null || z.gesprochen) return false;
  if (z.gemessenMs === null || z.tonLaeuft === true) return false;
  return z.offenSeitMs >= z.fensterMs && z.gemessenMs >= z.fensterMs;
}

/**
 * Wortlos-Frist für ein Mikro, das von selbst aufging (22.10.2026, Martin 07:52: „Ich habe nur getippt“).
 *
 * Nach einer Frage öffnet das Nachlausch-Fenster; Tipp- und Klickgeräusche zählen am Pegel als Sprache
 * (`nachlauschen-bleibt grund=gesprochen`), und die Aufnahme lief 33 s (08:44) bzw. 50 s (07:50), ohne dass die
 * Live-Mitschrift ein einziges Wort fand (`[diktat] erstesWort=- proben=0`). Darum: kam bis WORTLOS_MS kein Wort der
 * Live-Mitschrift und kein Text des Nachhörens, endet die Aufnahme — sie geht wie nach Stille hinaus, und der Server
 * verwirft Geräusch (Rede-Nachweis, Silbensalat-Prüfung server/ptt-salat.ts). Nie verworfen: hat Martin doch gesprochen
 * und lief die Live-Mitschrift nicht, geht nichts verloren (im Zweifel eine Sendung zu viel).
 *
 * ⚠️ Nur für ein von selbst geöffnetes Mikro (Nachlausch-Fenster) — nie für Taste, Pille, Wach-Wort oder Diktat.
 * ⚠️ Prüfauflage 22.10.: nur, wenn die Live-Mitschrift NACHWEISLICH lief (`liveLief`: eine Antwort von ihr kam an, auch eine
 *    leere, und das Nachhören hat sie nicht abgelöst). Kam nie etwas (nativer Weg, kein Guthaben, Leitung tot) oder hört gerade
 *    der Server nach, sagt „kein Wort“ nichts — dann bleibt das Mikro offen wie bisher.
 * ⚠️ Liefert das Nachhören Text, bleibt es offen — auch wenn danach die Mitschrift zurückkommt (`nachhoerText` ist gemerkt).
 */
export const WORTLOS_MS = 15_000;

export function wortlosZu(z: {
  /** Ging das Mikro von selbst auf (Nachlausch-Fenster)? */
  vonSelbst: boolean;
  /** Lief die Live-Mitschrift nachweislich (Antwort angekommen, nicht vom Nachhören abgelöst)? */
  liveLief: boolean;
  /** Seit dem Aufnahmestart; null = die Aufnahme läuft noch nicht. */
  offenSeitMs: number | null;
  /** Hat die Live-Mitschrift mindestens ein Wort erkannt? */
  liveWort: boolean;
  /** Hat das Nachhören Text geliefert? */
  nachhoerText: boolean;
}): boolean {
  return (
    z.vonSelbst && z.liveLief && z.offenSeitMs !== null && z.offenSeitMs >= WORTLOS_MS && !z.liveWort && !z.nachhoerText
  );
}

/*
 * PEGEL_LUECKE_MS (Ticket 2668) steht seit 28.09.2026 in mic-visualizer.ts:
 * nach derselben Lücke beginnt dort auch die Stille neu (Ticket 2697).
 */
export { PEGEL_LUECKE_MS };

/**
 * Der kurze Ton beim Öffnen des Nachlausch-Fensters landet im eigenen Mikro
 * — am Bluetooth mit Verzögerung. So lange zählt „laut" nicht als Sprache,
 * sonst hielte der eigene Ton das Fenster offen, und die Stille schickte
 * danach eine leere Aufnahme ab. (Ticket 2668)
 *
 * ⚠️ 29.09.2026: 600 ms waren zu kurz. Der Ton (880 Hz) liegt in den
 *    Aufnahmen der Fenster vom 27.–29.09. nicht am Anfang, sondern im
 *    Median bei 630 ms, in 58 von 96 Fällen zwischen 470 und 720 ms (am
 *    Lautsprecher, nach dem Umschalten von der Wiedergabe). Er zählte also
 *    genau nach der Schonfrist als Sprache: 148 von 211 Fenstern mit Text
 *    meldeten „gesprochen“ nach 0,74–1,05 s, und drei der verworfenen
 *    Sendungen (28.09. 10:00, 10:24, 10:48) tragen den Ton bei 590–670 ms
 *    und sonst nur Rauschen. 1 s deckt 93 von 96 Tönen; drei kamen erst nach
 *    1,3 s (zweimal Bluetooth). Ein Satz, der in der Schonfrist beginnt,
 *    zählt ab ihrem Ende; nachgerechnet an 135 Aufnahmen mit einem Wort der
 *    Mitschrift vor 2,8 s ging keine verloren.
 */
export const TON_SCHONFRIST_MS = 1000;

/**
 * Ab hier lernt im Nachlausch-Fenster der Grundpegel (dazu dessen üblicher
 * Anlauf von 100 ms). (29.09.2026, Ticket 2668)
 *
 * Die ersten Bilder eines frisch geöffneten Mikros liegen unter dem Rauschen
 * des Raums: am MacBook 150 ms lang Null (28.09. 09:04 und 21:45), am
 * iPhone oft die halbe Höhe. Das untere Zehntel weniger Bilder ist genau
 * dieser Anlauf — die Grenze lag dann bei ihrem Mindestwert, und das
 * gleichmäßige Rauschen des Raums galt als Ton („gesprochen“ nach 0,8 s,
 * der Server verwarf die Sendung als zu leise). In 162 von 181 Aufnahmen
 * ist der Anlauf nach 200 ms vorbei. Früher als die Schonfrist, damit ein
 * Satz, der sofort beginnt, nicht allein den Grund bestimmt.
 */
export const NACHLAUSCH_GRUND_AB_MS = 300;

/** So lange wartet der Bereit-Ton auf den Start des Rekorders (19.10.2026); kommt er nicht, bleibt der Ton aus. */
export const BEREIT_TON_WARTE_MS = 1500;

/*
 * Sprache am Pegel ist anhaltender Ton — die Regel und ihre Zahl stehen in
 * mic-visualizer.ts (TON_ANHALTEND_MS), wo derselbe Ton die Stille beendet.
 * Bis pre.3371 stand hier eine eigene Fassung (SPRACHE_AB_MS, Summe der
 * lauten Bilder); die Stille kannte sie nicht. (27.09.2026, Tickets 2668/2697)
 *
 * Gilt für die ganze Sprechtaste (`hatGesprochen` ist EINE Fassung): seit
 * 20.09. endeten vier Aufnahmen mit „gesprochen=ja", p90 unter der Grenze,
 * durch die Stille und ergaben „nichts verstanden" — zwei nach dem
 * Wach-Wort, eine nach einer Frage, eine im Nachlausch-Fenster.
 */

export interface PttModalCallbacks {
  /**
   * Warum das Fenster aufging, wenn es NICHT der eigene Druck war.
   * (23.09.2026, Ticket 2522 — Martin: „das muss differenziert werden".)
   * Ohne Angabe bleibt die Karte, wie sie war.
   */
  anlass?: string;
  /**
   * 17.10.2026: ging das Mikro nach einer FRAGE auf (`via=nach-frage`)? Dann geht eine kurze Zusage („Ja“) sofort hinaus
   * (shared/stille-auto.ts, `frageOffen`). Nie für ein Mikro, das der Nutzer selbst öffnete.
   */
  nachFrage?: boolean;
  /**
   * 17.10.2026 (Sicherheitsprüfung M2): wartet dabei eine ENTSCHEIDUNG (Ja-Tor, Rückfrage-Karte, Vorschlag, Frage des Schmieds)?
   * Dann gibt es die 400-ms-Abkürzung nicht: „Ja“ + Pause + „aber nicht an Anna“ führte sonst das Ja allein aus. Es bleibt bei der
   * alten Regel (mindestens 1,2 s, `AUTO_ZUSAGE_AB_MS`) oder der Sende-Taste. Fehlt die Angabe, gilt „ja“ (im Zweifel die sichere Seite).
   */
  entscheidungOffen?: boolean;
  /** User hat Senden gedrückt → senden. */
  onStop: () => void;
  /**
   * Beschriftung des Hauptknopfs. Standard: „Senden".
   *
   * 05.09.2026 — Wartet ein Bild im Eingabefeld, legt `onStop` das
   * Gesprochene DORT hinein, statt es allein zu verschicken (Martin: *„dann
   * soll es automatisch eingefügt werden, weil das die logische Konsequenz
   * ist"*). Ein Knopf, der dabei weiter „Senden" sagt, verspricht etwas
   * anderes, als er tut — deshalb darf der Aufrufer ihn umbenennen.
   */
  hauptLabel?: string;
  /**
   * User hat Verwerfen gedrückt → verwerfen. `grund` nur, wenn die Karte es
   * selbst tut und das Protokoll den Anlass nennen soll (Ticket 2668:
   * „nachlauschen-zu"); ohne Angabe war es ein Druck.
   * 19.10.2026: `quelle` nennt den Auslöser fürs Protokoll (knopf | hintergrund | escape | timer), nie Inhalt.
   */
  onCancel: (grund?: string, quelle?: string) => void;
  /**
   * Ticket 2634 (30.09.2026): Die Karte steht auf „Sende …“ und Martin blendet
   * sie aus — Knopf „Ausblenden“ oder Escape. Die Karte hat sich dann schon
   * selbst geschlossen. Der Zug läuft am Server weiter, NICHTS wird
   * abgebrochen oder neu gesendet; die Antwort kommt im Verlauf. Der Aufrufer
   * lässt nur seinen Verweis auf die Karte los. Ohne Rückruf geht die Karte
   * trotzdem zu.
   */
  onAusblenden?: () => void;
  /**
   * 2026-07-29 (Martin: "einen dritten Button, der 'Nur ins Textfeld
   * übertragen' steht, sodass ich noch den Text anpassen kann, falls ich ihn
   * nicht gleich wegschicken möchte oder Attachments hinzufügen will"):
   *
   * Bis hierher gab es nur zwei Wege aus einer Sprachnachricht: absenden
   * oder wegwerfen. Wer den Text noch ergänzen, korrigieren oder eine Datei
   * anhängen wollte, musste ihn abschicken und dann eine zweite Nachricht
   * schreiben — oder von vorn tippen.
   * Ohne Callback erscheint der Knopf nicht.
   */
  onToText?: () => void;
  /**
   * 2026-07-30 (Ticket 781): Steht im Eingabefeld schon ein Entwurf, ist das
   * Gesprochene womoeglich keine neue Nachricht, sondern eine ANWEISUNG dazu
   * ("mach das kuerzer"). Dieser Knopf erscheint nur dann — sonst waere er
   * ein Angebot ohne Gegenstand.
   *
   * Bewusst ein eigener Knopf und keine Erkennung im Text: Zu raten, ob
   * "mach das kuerzer" eine Anweisung oder der Anfang einer Nachricht ist,
   * geht irgendwann schief — und dann ist ein Entwurf weg. Ein Knopf sagt es
   * eindeutig.
   */
  onRewrite?: () => void;
  /**
   * 2026-07-22 (Martin: "stumm betreiben, wenn keiner mithören soll"):
   * Vorlesen an/aus umschalten. Liefert den NEUEN Stumm-Zustand
   * (true = Ausgabe stumm). Ohne Callback erscheint kein Knopf.
   */
  onToggleMute?: () => boolean;
  /**
   * Das Auto-Mikro direkt hier umschalten (Ticket 2403, Martin am 20.09.).
   *
   * *„Das waere nämlich schon wichtig, dass man das nicht nur in den
   * Einstellungen machen kann, sondern auch direkt."*
   *
   * ⚠️ Hier und NICHT im Dock. Martins stehende Regel vom 27.08.: die
   *    Fussleiste bleibt schmal, kein weiterer Platz in der Pille. Der
   *    Schalter gehoert ohnehin dorthin, wo man ihn braucht — ins
   *    Mikrofon-Fenster, neben das Stummschalten.
   *
   * Gibt den NEUEN Zustand zurueck, wie onToggleMute. Ohne Rueckruf
   * erscheint der Schalter nicht. Seit 23.09.2026 eine von drei Stufen
   * (AutoMikroStufe in settings.ts) statt an/aus.
   */
  onToggleAutoMikro?: () => AutoMikroStufe;
  /** Die Stufe des Auto-Mikros beim Oeffnen. */
  autoMikroInitial?: AutoMikroStufe;
  /**
   * „Absenden nach Stille" beim Öffnen, in Millisekunden (0 = aus, −1 = Auto).
   *
   * Die Karte ZEIGT nur, welcher Modus gilt — klein, ohne Knopf, ohne Fokus.
   * (30.09.2026, Ticket 2842; Martin: *„den Schalter für die Auto-Stille
   * herausnehmen … im Modal stattdessen nur klein und nicht bedienbar
   * anzeigen, welcher Stille-Modus gerade aktiv ist“*.) Umgestellt wird in
   * der Fußzeile und im Blatt der Antwortlänge (lib/stille-wahl.ts); beim
   * Sprechen klickt man sie ohnehin selten um. Bis 23.09.2026 (Ticket 2509)
   * war das hier ein Knopf mit dem Rückruf `onStilleWeiter`.
   *
   * Ohne Wert gibt es keine Anzeige. Die Aufnahme liest die Einstellung bei
   * jedem Takt frisch (`selbstSenden`), die Anzeige nur beim Öffnen.
   */
  stilleInitial?: number;
  /** Anfangszustand des Stumm-Knopfs (true = Ausgabe stumm). */
  mutedInitial?: boolean;
  /**
   * #533 (2026-07-24, Martin): Liefert den laufenden Mikro-Track fuer den
   * ECHTEN Pegel-Visualizer. NUR LESEND — die Aufnahme wird nie beruehrt.
   * null solange das Mikro noch startet (das Modal versucht es erneut).
   */
  getLevelTrack?: () => MediaStreamTrack | null;
  /**
   * Pegel aus der nativen Engine (05.09.2026): Läuft die Aufnahme dort,
   * gibt es keine Tonspur — die Balken bekommen ihren Wert alle 100 ms
   * von hier. null, solange kein nativer Lauf ist.
   */
  pegelNativ?: () => Promise<number | null> | null;
  /**
   * Ticket 2230 (12.09.2026): Hat die ECHTE Aufnahme bereits Ton — unabhängig
   * vom separaten Pegel-Analyser (eigener Kontext auf derselben Spur; er kann
   * bei kaltem Start/Bluetooth-Wechsel später Daten sehen als die echte
   * Aufnahme, oder gar keine, wenn sein Rechentakt steht). Liefert das true, wird die „kein Pegel"-Warnung unterdrückt,
   * egal was der Analyser gerade misst.
   */
  hatEchtenTon?: () => boolean;
  /**
   * Ticket 2618 (29.09.2026, nachgebessert): der laufende Teil
   * (PttRecorder.teilLage). Geht die Aufnahme in Teilen weiter, kündigt die
   * Karte den nächsten an. Gewarnt wird an der Uhr DIESES Teils — wie
   * bisher 30 s vor der Höchstdauer, wenn der nächste bis dahin nicht
   * begonnen hat. Ohne Rückruf (oder `null`) zählt die Karte selbst.
   */
  teilLage?: () => TeilLage | null;
  /**
   * Ticket 2420 (20.09.2026): von selbst absenden — per Ausloesewort oder
   * nach genug Stille.
   *
   * ⚠️ Das Fenster ENTSCHEIDET nicht, es MISST nur: hier liegen Pegel und
   *    Mitschrift beieinander. Die Regel steht in jetzt-senden.ts und ist
   *    ohne Mikrofon pruefbar.
   */
  selbstSenden?: () => SendeEinstellung | null;
  /**
   * „Absenden nach Stille: Auto" (Ticket 2763): fragt, ob die Mitschrift ein
   * fertiger Satz ist — Wahrscheinlichkeit 0…1, `null` bei jedem Ausfall
   * (dann gilt die feste Frist). Ohne Rückruf: immer die feste Frist.
   *
   * Ticket 2867 (01.10.2026): `stillMs` ist die Stille bei der Frage (für die
   * Journalzeile des Servers); die Antwort darf auch ein Objekt sein, das
   * sagt, ob die gestufte Schwelle gilt (`AutoAntwort`). Eine bloße Zahl
   * heißt: feste Schwelle, wie bisher.
   *
   * 03.10.2026 (Fantasiewörter): `rausch` — was die Karte zu den Endwörtern
   * gemessen hat (shared/rausch-woerter.ts); nur, wenn Wörter mit Zeiten da sind.
   *
   * 07.10.2026: `folge` — die wievielte Frage in dieser Pause und ob sich nur das
   * Satzzeichen am Ende änderte; nur für die Zeile des Servers (`frage=`, `erneut=`).
   */
  stilleAutoFrage?: (text: string, stillMs?: number, rausch?: RauschMessung, folge?: AutoFolge) => Promise<number | null | AutoAntwort>;
  /**
   * Wird gerufen, wenn die Karte von selbst handelt — abschicken, seit
   * 26.09.2026 auch verwerfen, ins Textfeld legen oder das Verworfene
   * zurückholen (Tickets 2659/2674, siehe SendeGrund). Beim Absenden per
   * Wort und beim Verwerfen sagt `wort`, ob es für sich stand (Sprechpause
   * in der Mitschrift) — nur dann fällt ein mehrdeutiges Wort wie „Weiter"
   * aus dem fertigen Text — und `wort.text`, was ohne den Befehl dasteht:
   * beim Verwerfen der Text, der eine Minute lang zurückzuholen ist.
   */
  onSelbstSenden?: (grund: SendeGrund, wort?: { fuerSich: boolean; text: string }) => void;
  /**
   * Ticket 2643 (26.09.2026 abends): läuft die Seite in der App-Hülle?
   * Nur für den Satz bei stummer Quelle — dort liegt das Mikrofon-Recht in
   * den iPhone-Einstellungen, im Browser bei der Seite.
   */
  inApp?: boolean;
  /**
   * Ticket 2643: die Quelle blieb auch nach dem Tausch stumm. Die Karte sagt
   * es, solange kein Ton kommt (`stummBisher`).
   *
   * ⚠️ 26.09.2026 spät (dritte Runde): Bis pre.3347 schickte der Aufrufer
   *    die Aufnahme dann nicht mehr zur Erkennung — kam der Ton später
   *    doch, war das Diktat verloren. Jetzt geht sie wie immer hin; der
   *    Server wirft echte digitale Stille selbst weg.
   */
  onStummeQuelle?: () => void;
  /**
   * Ticket 2643 (29.09.2026): darf die App das Mikrofon benutzen? Die Karte
   * fragt einmal, wenn die Quelle stumm bleibt, und macht aus dem Satz den
   * richtigen (mikro-recht.ts). Ohne Angabe: der Satz ohne Ursache.
   */
  mikroRecht?: () => Promise<MikroRecht>;
  /** Ticket 2643: öffnet die Einstellungen der App; false = geht hier nicht. */
  oeffneEinstellungen?: () => Promise<boolean>;
  /**
   * Ticket 2668: das Nachlausch-Fenster in Millisekunden — nur, wenn das
   * Mikro von selbst aufging. Ohne Angabe oder 0 bleibt die Karte offen wie
   * bisher. Siehe `nachlauschenZu`.
   */
  nachlauschMs?: number;
  /**
   * 19.10.2026: der leise Bereit-Ton, wenn das Mikro von selbst aufging (Nachlausch-Fenster) — `false` schaltet ihn ab
   * (Einstellung `bereitTon`). Ohne Angabe: an. Er klingt erst, wenn der Rekorder wirklich aufnimmt (rec-bereit.ts).
   */
  bereitTon?: boolean;
  /**
   * Ticket 2741 (Martin, 28.09.2026): die Mitschrift beim Sprechen anzeigen?
   * Ohne Angabe: ja. `false` blendet NUR die Anzeige aus — die Erkennung
   * läuft weiter, denn „Auto" (Ticket 2763), das Auslösewort und die Frage
   * „schon gesprochen?" lesen ihren Text. Statt des Satzes bekommt der
   * Pegel die Bühne (`.ohne-mitschrift` in style.css).
   */
  mitschriftZeigen?: boolean;
}

export interface PttModalHandle {
  /** Verbindungs-/Aufnahme-Status setzen. */
  setStatus(s: "starting" | "recording" | "sending"): void;
  /** Eingerasteter Freihand-Modus an/aus. */
  setLocked(active: boolean): void;
  /** Wave-Animation an/aus. */
  setSpeaking(active: boolean): void;
  /**
   * 21.10.2026 (Diktat aus dem Feld-Mikro, app.ts `feldDiktat`): diese Aufnahme endet im Eingabefeld, nie als Nachricht.
   * Der Hauptknopf heißt „Ins Feld“, der Zustand nach dem Stopp „Ins Feld …“ statt „Sende …“. Auch mitten in einer Aufnahme.
   */
  setInsFeld(): void;
  /**
   * 2026-08-28 (Martin: "ein Mikrofonzeichen"): Was die Mitschrift bisher
   * verstanden hat. Leerer Text blendet die Zeile aus — sie darf nicht als
   * leerer Kasten stehenbleiben, wenn die Erkennung gar nicht laeuft
   * (kein Guthaben, Netz weg).
   *
   * `hinweis`: nur anzeigen, nicht als Gesprochenes werten — für den Satz,
   * der nativ an Stelle der Mitschrift steht (24.09.2026, Ticket 2202). Er
   * steht leise da, nicht groß wie ein erkannter Satz (Ticket 2607).
   * `woerter`: die bestätigten Wörter mit den Zeitmarken der Erkennung —
   * daraus die Sprechpause vor einem Endwort (26.09.2026 spät, jetzt-senden.ts
   * `pauseAusZeiten`). `alleWoerter` (03.10.2026): dazu die vorläufigen, mit
   * Uhrzeit der Seite — nur fürs Urteil über Fantasiewörter (rausch-woerter.ts).
   */
  setLiveText(
    text: string,
    art?: { hinweis?: boolean; woerter?: readonly LiveWort[]; alleWoerter?: readonly LiveWort[] },
  ): void;
  /**
   * Ticket 2867 (01.10.2026): die Erkennung hat ein Satzende gemeldet (`<end>`).
   * NUR zum Messen (frontend/lib/ende-token.ts, Zeile `[ende-token]`) — ohne
   * Wirkung auf Text, Stille und Entscheidung.
   */
  setEndeToken(e: EndeSignal): void;
  /**
   * 12.10.2026 (Messfühler, nur Zahlen und feste Wörter): wo „Auto“ in diesem Moment stand —
   * ` stillMs=… jev=… satz=… autoRestMs=…` für die Zeile `[ptt-ende] senden` (frontend/app.ts), wenn die
   * Aufnahme NICHT durch Auto endet (Knopf, Wort …): so sieht man, wie lange Auto noch gebraucht hätte.
   * Leer, wenn „Auto“ nicht gewählt ist oder Jev für dieses Konto nicht gilt. Rein lesend.
   */
  autoStand(): string;
  /**
   * 12.10.2026 (Bahn „fortsetzung-anhaengen“): dieselbe Lage wie `autoStand`, aber als Zahlen — für die Fortsetzung (frontend/lib/fortsetzung.ts),
   * wenn die Aufnahme durch Stille endet: Jevs Urteil für den Schlusstext (`null`: keines), die Art des Satzendes und die Stille. Rein lesend.
   */
  /**
   * 15.10.2026 (Messfühler „wortstille“, nur Zahlen): ` textRuhtMs=… pegelSperreMs=…` für JEDE Zeile `[ptt-ende] senden` (frontend/app.ts) —
   * wie lange die Live-Mitschrift unverändert stand und wie viel davon der Pegel NICHT als Stille zählte (`pegelSperreMs` = Ruhe des
   * Textes minus Stille am Pegel, nie negativ: so lange hielt Ton ohne Wort das Senden auf). Leer ohne Live-Text (nativer Weg, Nachhören,
   * noch kein Wort). Rein lesend.
   */
  ruheStand(): string;
  autoZahlen(): { jev: number | null; satz: "?" | "." | "!" | "keins" | "-"; stillMs: number | null };
  /**
   * Ticket 2672 (26.09.2026): was das Nachhören aus der laufenden Aufnahme
   * gehört hat (frontend/lib/nachhoeren.ts). Ab dem ersten Aufruf kommen
   * Text und „wurde gesprochen" von dort. Die Stille am Ende nur, solange
   * der Pegel der Seite nicht misst (Ticket 2697, 28.09.2026: sonst hielten
   * erfundene Schnipsel aus der Stille das Senden auf, siehe `stilleJetzt`).
   * Der Text erscheint als Mitschrift, gekennzeichnet als nachgereicht.
   * `text` null: diesmal nichts erkannt, der bisherige bleibt.
   * `tonDavor` (26.09.2026 spät): lag vor dem Endstück schon Ton? Dann ist
   * es nie das ganze Diktat; ohne Angabe gilt „ja".
   */
  nachgehoert(p: { text: string | null; stilleMs: number; gesprochen: boolean; tonDavor?: boolean }): void;
  /**
   * 08.10.2026: der Pegel, den das Nachhören am Server aus der Aufnahme gemessen hat (dBFS je 100 ms, nur neue
   * Fenster). Die Anzeige zeigt ihn, solange sie selbst nicht misst — sonst nichts (mic-visualizer.ts).
   */
  pegelNachgereicht(db: readonly number[]): void;
  /** Ticket 2672: hat die Pegelanzeige ihren Rechentakt aufgegeben („tot")? */
  pegelTot(): boolean;
  /**
   * Ticket 2672 (26.09.2026 abends): seit wann laut Pegel gesprochen wird —
   * ab dem Moment, in dem der Pegel Sprache belegt (anhaltender Ton,
   * TON_ANHALTEND_MS, Tickets 2668/2697); `null`, solange nicht. Für die
   * Wache: kommt Sprache, aber keine Mitschrift, hängt die Verbindung zur
   * Erkennung.
   */
  sprichtSeitMs(): number | null;
  /**
   * Ticket 2672 (26.09.2026 abends): die Live-Mitschrift liefert wieder —
   * das Nachhören endet, Anzeige und Entscheidung gehören wieder ihr.
   */
  mitschriftZurueck(): void;
  /**
   * Ticket 2643 (26.09.2026 spät): meldete die Sprechtaste eine stumme
   * Quelle, und kam seitdem am Pegel kein Laut? Nur dann gehört der Satz
   * „Das Mikrofon liefert nur Stille …" auch nach dem Ende in den Verlauf.
   */
  stummBisher(): boolean;
  /**
   * Ticket 2668 (27.09.2026): öffnete diese Aufnahme das Nachlausch-Fenster,
   * und war bis jetzt KEINE Sprache belegt (`hatGesprochen`)? Nur dann
   * schließt sie still, wenn die Erkennung nichts liefert (app.ts,
   * pttLeerStill). War Sprache belegt, ist ein leeres Ergebnis ein echter
   * Erkennungsausfall — dann bleibt die Rückmeldung.
   */
  nachlauschOhneSprache(): boolean;
  /**
   * Ticket 2618 (29.09.2026): Teil `nr` hat begonnen, der vorige ist
   * aufgehoben. Die Hinweiszeile sagt es.
   */
  teilBegonnen(nr: number): void;
  /**
   * 30.10.2026 (Aufnahme-Wächter, ptt-wachter.ts): die Aufnahme ist ausgefallen
   * (`ausfall`: Karte rot, Alarm) oder die Seite kam nach einer Lücke zurück
   * (`rueckkehr`: ehrlicher Satz, ohne Alarm). Der Satz steht in der Hinweiszeile.
   */
  zeigeAusfall(satz: string, art: "ausfall" | "rueckkehr"): void;
  /** Modal schließen + DOM aufräumen. */
  close(): void;
}

export function openPttModal(cb: PttModalCallbacks): PttModalHandle {
  const overlay = document.createElement("div");
  overlay.className = "ptt-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Sprachnachricht aufnehmen");

  const card = document.createElement("div");
  card.className = "ptt-modal-card";
  /** Ticket 2741: Mitschrift ausgeblendet — der Leuchtbalken trägt die Karte. */
  const ohneMitschrift = cb.mitschriftZeigen === false;
  card.classList.toggle("ohne-mitschrift", ohneMitschrift);

  // ── Kopf: Puls-Punkt + Status-Wort links, Timer rechts ────────────────
  const head = document.createElement("div");
  head.className = "pttm-head";
  /*
   * Die Hinweiszeile unter dem Kopf: warum das Fenster aufging (Ticket 2522)
   * und nach einem Tipp aufs Auto-Mikro, was jetzt gilt (Ticket 2403,
   * 23.09.2026). EINE Zeile für beides, entsteht erst, wenn es etwas zu
   * sagen gibt.
   */
  let hinweisEl: HTMLParagraphElement | null = null;
  /**
   * Der Satz, der gerade in der Zeile steht — GANZ, nicht die Kurzfassung. Alle
   * Vergleiche („steht meine Meldung noch da?“) laufen über ihn, nie über den
   * Text des Elements: steht dort eine Kurzfassung samt Vorleser-Satz, wäre der
   * Text des Elements nicht mehr der Satz. (Ticket 2842, 01.10.2026)
   */
  let hinweisSatz: string | null = null;
  const zeigeHinweis = (text: string): void => {
    if (!hinweisEl) {
      hinweisEl = document.createElement("p");
      hinweisEl.className = "pttm-anlass";
      hinweisEl.setAttribute("role", "status");
      head.after(hinweisEl);
    }
    hinweisSatz = text;
    const kurz = hinweisKurz(text);
    if (kurz === null) {
      hinweisEl.textContent = text;
      return;
    }
    // Sichtbar die Kurzfassung, für Vorleser der ganze Satz — wie bei „Stille …“ (.pttm-stille).
    const sichtbar = document.createElement("span");
    sichtbar.setAttribute("aria-hidden", "true");
    sichtbar.textContent = kurz;
    const fuerVorleser = document.createElement("span");
    fuerVorleser.className = "pttm-nur-vorleser";
    fuerVorleser.textContent = text;
    hinweisEl.replaceChildren(sichtbar, fuerVorleser);
  };
  /**
   * Eine vorübergehende Meldung zurücknehmen (Tickets 2668/2674): nur, wenn
   * sie noch dasteht — hat inzwischen etwas anderes die Zeile übernommen,
   * bleibt das. `vorher` null: die Zeile gab es nicht, sie geht wieder.
   */
  const nimmHinweisZurueck = (meldung: string, vorher: string | null): void => {
    if (hinweisEl === null || hinweisSatz !== meldung) return;
    if (vorher !== null) {
      zeigeHinweis(vorher);
      return;
    }
    hinweisEl.remove();
    hinweisEl = null;
    hinweisSatz = null;
  };
  const state = document.createElement("div");
  state.className = "pttm-state";
  const dot = document.createElement("span");
  dot.className = "pttm-dot";
  state.appendChild(dot);
  const stateLabel = document.createElement("span");
  stateLabel.className = "pttm-state-label";
  stateLabel.textContent = "Öffne Mikro …";
  state.appendChild(stateLabel);
  head.appendChild(state);
  const headRight = document.createElement("div");
  headRight.className = "pttm-head-right";
  const tools = document.createElement("div");
  tools.className = "pttm-tools";
  tools.setAttribute("role", "group");
  tools.setAttribute("aria-label", "Ton und Mikro");
  if (cb.onToggleMute) {
    const muteBtn = document.createElement("button");
    muteBtn.type = "button";
    muteBtn.className = "pttm-mute";
    const syncMute = (muted: boolean): void => {
      muteBtn.classList.toggle("is-muted", muted);
      muteBtn.setAttribute(
        "aria-label",
        muted ? "Vorlesen einschalten" : "Vorlesen stummschalten",
      );
      muteBtn.title = muted
        ? "Stumm — Antwort wird nicht vorgelesen"
        : "Antwort wird vorgelesen — tippen für stumm";
    };
    muteBtn.appendChild(speakerIcon());
    syncMute(cb.mutedInitial === true);
    muteBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      syncMute(cb.onToggleMute ? cb.onToggleMute() : false);
    });
    tools.appendChild(muteBtn);
  }
  /*
   * Ticket 2403 — das Auto-Mikro direkt hier umschalten.
   *
   * Neben dem Stummschalten, weil beides dasselbe betrifft: ob die Maschine
   * von selbst redet, und ob sie von selbst zuhoert.
   *
   * 23.09.2026: drei Stufen, ein Tipp schaltet weiter (aus → nach einer
   * Frage → Jev entscheidet → aus; „Jev entscheidet“ ersetzt seit 07.10.
   * „nach jeder Antwort“, Ticket 3048). Am Telefon gibt es keinen Tooltip:
   * darum sagt die Hinweiszeile nach jedem Tipp, was jetzt gilt, und die
   * dritte Stufe ist gefüllt statt nur getönt.
   */
  if (cb.onToggleAutoMikro) {
    const autoBtn = document.createElement("button");
    autoBtn.type = "button";
    autoBtn.className = "pttm-auto";
    const syncAuto = (stufe: AutoMikroStufe): string => {
      const t = AUTO_MIKRO_TEXT[stufe === "jev" ? "jev" : stufe ? "frage" : "aus"];
      autoBtn.classList.toggle("is-an", stufe !== false);
      autoBtn.classList.toggle("is-jev", stufe === "jev");
      autoBtn.setAttribute("aria-pressed", String(stufe !== false));
      autoBtn.setAttribute("aria-label", t.label);
      autoBtn.title = t.titel;
      return t.hinweis;
    };
    autoBtn.appendChild(autoMikroIcon());
    syncAuto(cb.autoMikroInitial ?? false);
    autoBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      zeigeHinweis(syncAuto(cb.onToggleAutoMikro ? cb.onToggleAutoMikro() : false));
    });
    tools.appendChild(autoBtn);
  }
  // Beide Bedienknöpfe in EINER Gruppe (gleich groß, gleich ausgerichtet) — vor der Stille-Anzeige und der Uhr.
  if (tools.childElementCount > 0) headRight.appendChild(tools);
  /*
   * Ticket 2509 → 2842: „Absenden nach Stille" neben dem Auto-Mikro — seit
   * 30.09.2026 nur noch die Anzeige, welcher Modus gilt (Auto, 2 s, aus).
   * Kein Knopf, kein Klick, kein Fokus: ein Text, der wie ein Knopf aussähe,
   * wäre einer, der nichts tut. Der Vorleser bekommt den ganzen Satz statt
   * des Kurztexts („Stille 3 s" allein sagt nicht, WAS drei Sekunden dauert).
   * Kein role="status": der Wert ändert sich in der Karte nicht.
   */
  if (cb.stilleInitial !== undefined) {
    const ms = cb.stilleInitial;
    const an = ms > 0 || ms === STILLE_AUTO_MS;
    const stilleAnzeige = document.createElement("span");
    stilleAnzeige.className = "pttm-stille";
    const kurz = document.createElement("span");
    kurz.setAttribute("aria-hidden", "true");
    kurz.textContent = an ? `Stille ${stilleKurz(ms)}` : "Stille aus";
    const fuerVorleser = document.createElement("span");
    fuerVorleser.className = "pttm-nur-vorleser";
    fuerVorleser.textContent =
      ms === STILLE_AUTO_MS
        ? "Absenden nach Stille: Auto. Nach einer kurzen Pause wird geprüft, ob dein Satz fertig ist."
        : an
          ? `Absenden nach Stille: ${Math.round(ms / 1000)} Sekunden.`
          : "Absenden nach Stille: aus.";
    stilleAnzeige.append(kurz, fuerVorleser);
    headRight.appendChild(stilleAnzeige);
  }
  const timerEl = document.createElement("span");
  timerEl.className = "pttm-timer";
  timerEl.textContent = "0:00";
  headRight.appendChild(timerEl);
  head.appendChild(headRight);
  card.appendChild(head);
  if (cb.anlass) zeigeHinweis(cb.anlass);

  // ── Wellenform ────────────────────────────────────────────────────────
  // 2026-07-25 (Ticket 562): derselbe Visualizer wie in der Eingabeleiste —
  // eine Implementierung, ein Verhalten. Er zeigt den echten Pegel als
  // durchlaufenden Verlauf statt Frequenzbaender, von denen die oberen bei
  // Sprache ohnehin still stehen.
  /*
   * ⚠️ 2026-08-29 — die Karte sagt jetzt, wenn KEIN Pegel ankommt.
   *
   * Martin: *„Der Visualizer geht auch nicht [...] es verwirrt, weil man
   * nicht weiss, ob überhaupt irgendwas funktioniert."* Auf seinem Foto
   * standen alle Balken auf dem Ruhewert. Eine flache Linie sieht aus wie
   * eine arbeitende Anzeige — sie ist aber genau dann nichtssagend, wenn
   * man sie am dringendsten braucht.
   *
   * Die Aufnahme laeuft dabei WEITER: der Server erkennt hinterher ohnehin
   * (im Protokoll: „stt 302ms transcript=43ch"). Der Hinweis nimmt nur die
   * Ungewissheit, er bricht nichts ab.
   */
  /*
   * Ticket 2420: was die Entscheidung „jetzt senden" braucht. Beides liegt
   * nur HIER beieinander — der Pegel im Visualizer, die Mitschrift in
   * setLiveText.
   */
  let stillMs = 0;
  /** Ticket 2103/2420: die Warnung meldet sich beim WECHSEL, nicht je Bild. */
  let warKeinPegel = false;
  let letzterText = "";
  let textSeit = 0;
  let sendeTimer = 0;
  let schonGesendet = false;
  /*
   * Ticket 2420/2403: kam in dieser Aufnahme schon Stimme? Siehe SendeLage.
   * Tickets 2668/2697 (27.09.2026): am Pegel anhaltender Ton (mic-visualizer.ts,
   * TON_ANHALTEND_MS); was das Nachhören aus dem Ton am Server hörte, getrennt.
   */
  let tonBelegt = false;
  let nachhoerHoerte = false;
  /** Ticket 2672: wann der Pegel zum ersten Mal Stimme belegte (performance.now). */
  let stimmeSeit: number | null = null;
  /*
   * Ticket 2672 (26.09.2026): das Nachhören. Solange null, gilt alles wie
   * bisher. Hat es einmal geliefert, ist es die EINE Quelle für den Text
   * (siehe sendeLage) — die Mitschrift der Seite steht dann ohnehin, und
   * zwei Schreiber auf `letzterText` wären zwei Besitzer. Die Stille gehört
   * ihm nur, solange der Pegel nicht misst (`stilleJetzt`, Ticket 2697).
   */
  let nachhoer: { stilleMs: number; tonDavor: boolean } | null = null;
  /** Ticket 2672: welcher Satz beim Umschalten dastand, und welcher vorher. */
  let nachhoerSatz: string | null = null;
  let nachhoerVorher: string | null = null;
  /** Ticket 2672: die Pegelanzeige hat ihren Rechentakt aufgegeben. */
  let pegelAufgegeben = false;
  /** Ticket 2659: wann sich die Mitschrift änderte — für die Sprechpause. */
  let textVerlauf = neuerTextVerlauf();
  /*
   * Ticket 2643: die Quelle blieb stumm — gesagt, und seitdem kein Laut?
   * (26.09.2026 spät: ein späterer Laut nimmt den Satz zurück.)
   */
  let stummGesagt = false;
  let stummWiderlegt = false;
  let stummSatz: string | null = null;
  let stummVorher: string | null = null;
  /*
   * 29.09.2026 (Ticket 2643): Die Quelle meldete sich stumm, bevor die Karte
   * auf „recording“ stand — Konto 23 (Mikro-Start bis 7 s): dreimal
   * `ptt-stumme-quelle-bleibt` im Journal, aber nie `hinweis-stummes-mikro`.
   * Der Satz fiel still weg. Jetzt wartet er auf den Aufnahmebeginn.
   */
  let stummAusstehend = false;
  let einstellungenKnopf: HTMLButtonElement | null = null;
  /*
   * Ticket 2763: „Auto" — Jevs letztes Urteil und die laufende Frage, je
   * Aufnahme neu. Eine Antwort zählt nur für den Text, zu dem sie gefragt
   * wurde; eine überholte Frage wird verworfen, wenn sie ankommt.
   */
  let autoUrteil: AutoUrteil | null = null;
  let autoFrage: { text: string; seit: number } | null = null;
  /** 12.10.2026 (Messfühler `[ende-token] frageMs=`): die Stille, bei der die Frage zum jetzigen Urteil gestellt wurde. */
  let urteilFrageMs: number | null = null;
  /*
   * 07.10.2026 (Komma am Ende): die Fragen in DIESER Pause — wie viele und über welchen
   * Text die letzte war. Nur für die Zeile des Servers (`frage=`, `erneut=`); jede neue
   * Pause (Stille unter AUTO_FRAGE_FRUEHESTENS_MS) beginnt bei null. Gefragt wird wie vorher je Text.
   */
  let pauseFragen = 0;
  let pauseLetzterText: string | null = null;
  /*
   * 03.10.2026 (Fantasiewörter, shared/rausch-woerter.ts): woran die Karte
   * Endwörter aus dem Rauschen erkennt — der Pegel der Anzeige (nur gelesen),
   * der Beginn der laufenden Stille und die Wörter mit Uhrzeit. Ob der Server
   * sie fürs Urteil weglässt, sagt seine letzte Antwort (`rauschFilter`,
   * Schalter STILLE_RAUSCH_FILTER); bis dahin zählt der volle Text.
   */
  const pegelVerlauf = neuerPegelVerlauf();
  let stilleBeginn: number | null = null;
  let letzteGrenze: number | null = null;
  let alleWoerter: readonly LiveWort[] = [];
  let rauschFilterGilt = false;
  /**
   * 07.10.2026: darf die Anzeige die Sprachgrenze anwenden (mic-visualizer.ts)? Nur ein
   * ausdrückliches `sprachgrenze: true` in der letzten Antwort des Servers (Schalter
   * `STILLE_SPRACHGRENZE`) — und nur im Auto-Modus, nur dort fragt die Karte.
   */
  let sprachgrenzeGilt = false;
  /** 07.10.2026: steht „Absenden nach Stille“ in diesem Takt auf Auto? Je Takt neu (die Wahl kann mitten in der Aufnahme wechseln). */
  let autoModus = false;
  /*
   * ⚠️ Prüfbefund 03.10.2026: `k` wird je Takt neu gemessen und fiel für einen
   *    Takt auf 0, sobald das letzte Bild laut war (stilleBeginn weg) oder nicht
   *    gemessen. Dann passte Jevs Urteil (am Text OHNE die Wörter) nicht mehr,
   *    und Auto schickte über den Rückfall mitten in der Denkpause ab. Darum:
   *    was für DIESEN Text einmal als Rauschen zählte, bleibt es, bis sich der
   *    Text ändert — `k` wächst je Text nur.
   */
  let rauschFest: { text: string; k: number } | null = null;
  const rauschJetzt = (text: string): RauschMessung => {
    const roh = text.trim();
    const r = rauschBefund(roh, alleWoerter, pegelVerlauf, stilleBeginn, letzteGrenze);
    const k = rauschFest?.text === roh ? Math.max(rauschFest.k, r.k) : r.k;
    rauschFest = { text: roh, k };
    return k === r.k ? r : { ...r, k };
  };
  /** Der Text, über den Jev urteilt — ohne Fantasiewörter am Ende, wenn der Server sie weglässt. */
  const urteilsText = (text: string, r: RauschMessung = rauschJetzt(text)): string =>
    rauschFilterGilt ? ohneEndWoerter(text, r.k) : text.trim();
  /*
   * Ticket 2842 (30.09.2026): die kleine Zeile „Jev: fertig 0,93 · senden" —
   * Martin will sehen, ob und wann Jev abgesendet hätte. Nur Anzeige, nur
   * Konto 1 (wie die Frage selbst), und sie gilt nur für den Text, zu dem Jev
   * geantwortet hat: mit dem nächsten Text geht sie, mit der Karte auch. Die
   * Zahl ist die des Servers (`fertig`), nichts davon ist geraten.
   */
  const jevEl = document.createElement("p");
  jevEl.className = "pttm-jev";
  jevEl.hidden = true;
  /*
   * ⚠️ Nachprüfung 01.10.2026: Im Modus Auto und für Konto 1 bleibt der PLATZ der Zeile
   * reserviert (is-leer = unsichtbar, aber im Fluss). Mit `hidden` kam und ging die Zeile in
   * jeder Sprechpause — 28 Punkte, um die der Pegel und der Hinweis darunter sprangen.
   */
  const platzHalten = (): boolean => jevMoeglich() && cb.stilleInitial === STILLE_AUTO_MS;
  const zeigeJev = (): void => {
    const u = autoUrteil;
    const gilt = u !== null && jevMoeglich() && u.text === urteilsText(letzterText);
    // 02.10.2026: mit der Satzzeichen-Regel (nur wenn der Server sie meldet) sagt die Zeile die frühere Zeit.
    // 16.10.2026: … und mit dem offenen Ende (`urteilEnde`, ohne Satzzeichen strenger) die spätere.
    const zeile = gilt
      ? jevUrteilZeile(u.fertig, u.stufen === true, u.satzzeichen === true ? satzEnde(u.text) : null, tempoLesen(u.tempo), urteilEnde(u))
      : "";
    if (platzHalten()) {
      jevEl.hidden = false;
      jevEl.classList.toggle("is-leer", zeile === "");
      const text = zeile === "" ? "\u00a0" : zeile;
      if (jevEl.textContent !== text) jevEl.textContent = text;
      return;
    }
    jevEl.classList.remove("is-leer");
    if (jevEl.textContent !== zeile) jevEl.textContent = zeile;
    jevEl.hidden = zeile === "";
  };
  zeigeJev();
  /**
   * `text`: der Schlüssel (urteilsText), `roh`: die ganze Mitschrift — die geht
   * an den Server, mit der Zahl der Fantasiewörter; weggelassen werden sie dort.
   */
  const frageAuto = (text: string, roh: string, rausch: RauschMessung): void => {
    const frage = { text, seit: performance.now() };
    autoFrage = frage;
    const stille = Math.round(stilleJetzt());
    const mitWoertern = rausch.pegel.length > 0;
    // 07.10.2026: die wievielte Frage in dieser Pause — und ob nur das Satzzeichen am Ende neu ist.
    const folge: AutoFolge = { frage: ++pauseFragen, erneut: pauseLetzterText !== null && nurSatzendeNeu(pauseLetzterText, text) };
    pauseLetzterText = text;
    const antwort = cb.stilleAutoFrage
      ? cb.stilleAutoFrage(roh, stille, mitWoertern ? rausch : undefined, folge)
      : Promise.resolve(null);
    void antwort
      .catch(() => null)
      .then((a) => {
        if (autoFrage !== frage) return;
        /*
         * Ticket 2867: nur eine Antwort, die ausdrücklich „Stufen gelten“
         * sagt, schaltet die gestufte Schwelle ein; alles andere (eine bloße
         * Zahl, ein Ausfall) lässt die feste 0,8 — im Zweifel das alte Verhalten.
         */
        const fertig = typeof a === "number" ? a : a && typeof a === "object" ? a.fertig : null;
        // 03.10.2026: nur ein ausdrückliches „ja“ des Servers lässt die Fantasiewörter weg.
        rauschFilterGilt = typeof a === "object" && a !== null && a.rauschFilter === true;
        // 07.10.2026: ebenso die Sprachgrenze — ohne Antwort (Ausfall, bloße Zahl) der alte Weg.
        sprachgrenzeGilt = typeof a === "object" && a !== null && a.sprachgrenze === true;
        const urteil: AutoUrteil = {
          // Worüber Jev wirklich urteilte: dieselbe Regel wie am Server.
          text: rauschFilterGilt ? ohneEndWoerter(roh, rausch.k) : roh.trim(),
          fertig: typeof fertig === "number" ? fertig : null,
          stufen: typeof a === "object" && a !== null && a.stufen === true,
          // 02.10.2026: ebenso nur ein ausdrückliches „ja“ schaltet die Satzzeichen-Regel ein.
          satzzeichen: typeof a === "object" && a !== null && a.satzzeichen === true,
          // 05.10.2026: … und „Laut ohne neues Wort“ (shared/stille-auto.ts, autoJevStille) …
          textruhe: typeof a === "object" && a !== null && a.textruhe === true,
          // 07.10.2026: … und „Komma am Ende“ (shared/stille-auto.ts, kommaAmEnde).
          komma: typeof a === "object" && a !== null && a.komma === true,
          // 08.10.2026: … und das „mittlere Urteil“ (AUTO_MITTEL_HOECHSTENS_MS).
          mittel: typeof a === "object" && a !== null && a.mittel === true,
          // 12.10.2026: … der Feinschliff (sofortige Auswertung; früh fragen) …
          fein: typeof a === "object" && a !== null && a.fein === true,
          // … und der kurze Deckel (AUTO_GESCHLOSSEN_HOECHSTENS_MS, Standard am Server aus).
          deckelKurz: typeof a === "object" && a !== null && a.deckelKurz === true,
          // 16.10.2026: … und die kürzere Obergrenze nach „?“ bei Jev 0,3–0,49 (AUTO_FRAGE_HOECHSTENS_MS, Standard am Server an).
          frageKurz: typeof a === "object" && a !== null && a.frageKurz === true,
          // 16.10.2026: … und das offene Ende (ohne Satzzeichen strenger, shared/stille-auto.ts offenesEnde; Standard am Server an).
          offenStreng: typeof a === "object" && a !== null && a.offenStreng === true,
          // 16.10.2026: … und die kurze Obergrenze 4 s / 6 s (shared/stille-auto.ts obergrenzeKurzMs; Standard am Server an).
          obergrenzeKurz: typeof a === "object" && a !== null && a.obergrenzeKurz === true,
          // 12.10.2026: … und die Tempo-Stufe — NUR zusammen mit `fortsetzung: true` aus derselben Antwort (ein schnelleres Senden gibt es nur,
          // solange ein Abschneiden angehängt wird); sonst `ruhig`, die Stufen von vorher.
          tempo: typeof a === "object" && a !== null && a.fortsetzung === true ? tempoLesen(a.tempo) : "ruhig",
          // 15.10.2026: … und „kurze Zusage“ (shared/stille-auto.ts, kurzeZusage).
          zusage: typeof a === "object" && a !== null && a.zusage === true,
          // 15.10.2026: … und „Wortstille“ (shared/stille-auto.ts, autoWortStille).
          wortruhe: typeof a === "object" && a !== null && a.wortruhe === true,
          // 17.10.2026: … und ihr Aufschub bei klar unfertigem Satz unter Ton (shared/stille-auto.ts, wortRuheZaehlt).
          wortruheUnfertig: typeof a === "object" && a !== null && a.wortruheUnfertig === true,
        };
        // Die letzte ausdrückliche Antwort des Servers gilt für die nächsten Fragen (ein Ausfall ändert nichts).
        if (typeof a === "object" && a !== null && typeof a.fein === "boolean") feinZuletzt = a.fein;
        // 12.10.2026: ebenso die Fortsetzung — nur eine ausdrückliche Antwort des Servers ändert den Stand, ein Ausfall nicht.
        if (typeof a === "object" && a !== null && typeof a.fortsetzung === "boolean") fortsetzungZuletzt = a.fortsetzung;
        // 15.10.2026: ebenso „kurze Zusage“.
        if (typeof a === "object" && a !== null && typeof a.zusage === "boolean") zusageZuletzt = a.zusage;
        // 15.10.2026: ebenso „Wortstille“.
        if (typeof a === "object" && a !== null && typeof a.wortruhe === "boolean") wortruheZuletzt = a.wortruhe;
        autoUrteil = urteil;
        urteilFrageMs = stille;
        autoFrage = null;
        zeigeJev();
        /*
         * 12.10.2026 (Feinschliff): die Antwort ist da — JETZT auswerten, nicht erst im nächsten 250-ms-Takt
         * (im Mittel 125 ms, bis 250 ms Wartezeit nach dem Absenden). Dieselbe Funktion wie der Takt
         * (`selbstSendenPruefen`), dieselbe reine Regel (autoPruefung), keine neue Logik. Nur, wenn der Server
         * es meldet (`fein`) und der Takt noch läuft (`sendeTimer`): nach einem Knopfdruck („Sende …“), einem
         * Absenden und dem Schließen der Karte ist er gestoppt (`stoppeSelbstSenden`), und nichts wertet mehr
         * aus. Ein zweites Absenden schließt `schonGesendet` aus (es wird vor dem Absenden gesetzt, im selben Zug).
         */
        if (urteil.fein === true && sendeTimer !== 0) selbstSendenPruefen(true);
      });
  };
  /** Ticket 2674: wartet gerade ein „Verwerfen" auf seine Ruhe? */
  let verwerfenWartet = false;
  let verwerfenVorher: string | null = null;
  /** 20.10.2026: die Vollständigkeits-Bremse dieser Aufnahme (shared/satz-vollstaendig.ts) und die Zeile davor. */
  const bremse = neueBremse();
  let bremseVorher: string | null = null;
  /*
   * Ticket 2668: das Nachlausch-Fenster. „an" ab dem Aufnahmestart, bis
   * gesprochen wurde oder es schließt. Dazu, seit wann der Pegel ohne Lücke
   * misst. (Bis wann der eigene Ton nicht als Sprache zählt, weiß seit
   * 27.09. die Anzeige selbst: viz.tonZaehltAb.)
   */
  let nachlausch: "aus" | "an" | "vorbei" = "aus";
  /** 22.10.2026 (Wortlos-Frist): hat die Live-Mitschrift in dieser Aufnahme mindestens ein Wort erkannt? */
  let liveWortGehoert = false;
  /** Prüfauflage 22.10.: kam in dieser Aufnahme eine Antwort der Live-Mitschrift an (auch eine leere, nie ein Hinweis)? */
  let liveAntwortKam = false;
  /** Prüfauflage 22.10.: hat das Nachhören in dieser Aufnahme Text geliefert? Bleibt gemerkt, auch nach mitschriftZurueck. */
  let nachhoerTextKam = false;
  /** 22.10.2026: die Wortlos-Frist hat einmal entschieden. */
  let wortlosEntschieden = false;
  let nachlauschVorher: string | null = null;
  let nachlauschStandGemeldet = false;
  /** Ticket 2668 (29.09.2026): liegt das letzte Bild in einem lauten Stück? */
  let tonLaeuft = false;
  let pegelSeit: number | null = null;
  let letztesPegelBild = 0;
  /** 05.10.2026: wann das vorige Bild kam (gemessen oder nicht) — für „Ton ohne neues Wort“. */
  let tonBildVorher: number | null = null;
  /** 20.10.2026: seit wann jedes gemessene Bild bei DAUERLAERM_PEGEL liegt (null = nicht) — für den Notausgang. */
  let dauerLautSeit: number | null = null;
  /** 20.10.2026: schickte der Notausgang ab — mit welcher Dauer des Dauerlärms (ms)? null = nicht. Fürs Journal. */
  let notausgangMs: number | null = null;
  /**
   * Wie lange schon Dauerlärm (jedes Bild ≥ DAUERLAERM_PEGEL)? 0, wenn keiner — und 0, sobald seit PEGEL_LUECKE_MS
   * kein Bild mehr kam (Seite verdeckt, Bildtakt steht, nativer Pegel aus): wer nicht misst, weiß nicht, ob es noch
   * laut ist (Prüfauflage 20.10.; derselbe Gedanke wie `pegelGemessenMs`).
   */
  const dauerLautMs = (jetzt = performance.now()): number =>
    dauerLautSeit === null || jetzt - letztesPegelBild > PEGEL_LUECKE_MS ? 0 : jetzt - dauerLautSeit;
  /** Misst der Pegel gerade — und seit wann ohne Lücke? */
  const pegelGemessenMs = (): number | null => {
    const jetzt = performance.now();
    return pegelSeit !== null && jetzt - letztesPegelBild <= PEGEL_LUECKE_MS
      ? jetzt - pegelSeit
      : null;
  };
  /*
   * „Wurde schon gesprochen?" — EINE Fassung für die Entscheidung UND für
   * die Messzeile `[ptt-stille]`. (23.09.2026, Tickets 2420/2512,
   * Nachprüfung: die Messung zählte nur `schonLaut`, die Entscheidung auch
   * den Text. Zwei Fassungen derselben Bedingung.)
   * 27.09.2026 (Tickets 2668/2697): am Pegel zählt nur anhaltender Ton, kein
   * einzelnes Bild — für die ganze Sprechtaste, siehe TON_ANHALTEND_MS.
   */
  const hatGesprochen = (): boolean => tonBelegt || nachhoerHoerte || letzterText.trim() !== "";
  /**
   * Wie lange es still ist — DIE Stelle für die Entscheidung. (Ticket 2697,
   * 28.09.2026)
   *
   * Misst der Pegel der Seite, gilt seine Stille, auch beim Nachhören. Nur
   * wenn er nicht misst (Rechentakt steht, Seite verdeckt), die Stille der
   * letzten Probe.
   *
   * ⚠️ Fall 16:35 (Campingplatz, „totenstill"): Die Mitschrift stand, der
   *    Pegel maß. Entschieden hat die Probe — am Server endet die Stille an
   *    jedem 100-ms-Fenster über dem Grundrauschen (stilleAmEnde). Leise
   *    Geräusche kamen alle 1–3 s, die Proben meldeten 100/100/300/100/1500
   *    ms Stille und dazu erfundene Schnipsel (13/14/8/1/1 Zeichen). Nach
   *    15 s Pause schickte nichts ab.
   */
  const stilleJetzt = (): number =>
    nachhoer && pegelGemessenMs() === null ? nachhoer.stilleMs : stillMs;
  /**
   * Was die Regel sieht (jetzt-senden.ts). Im Nachhör-Modus kommt die Ruhe
   * nach dem letzten Wort aus dem Ton: der Server hat die Aufnahme bis zur
   * Probe erkannt — nach dem Text kam also genau so lange nichts, wie es am
   * Ende still ist. Hochgerechnet wird nicht: ob zwischen zwei Proben wieder
   * gesprochen wurde, weiß erst die nächste.
   */
  const sendeLage = (): SendeLage =>
    nachhoer
      ? {
          text: letzterText,
          stillMs: stilleJetzt(),
          textRuhtMs: nachhoer.stilleMs,
          gesprochen: hatGesprochen(),
          // 26.09.2026 spät: das Endstück ist nur dann das ganze Diktat,
          // wenn davor kein Ton war (der Server sagt es).
          tonDavor: nachhoer.tonDavor,
          // 20.10.2026: die Ruhe des Textes zählt hier nicht (jetzt-senden.ts, textRuhtGenug) — der Pegel entscheidet.
          nachgehoert: true,
        }
      : {
          text: letzterText,
          stillMs: stilleJetzt(),
          textRuhtMs: performance.now() - textSeit,
          gesprochen: hatGesprochen(),
          dauerLautMs: dauerLautMs(),
          // Ticket 2659: nur die Live-Mitschrift kommt Wort für Wort.
          pauseVor: (davor) => textVerlauf.pauseVor(davor),
        };
  /**
   * Ticket 2672: der Satz, wenn die Anzeige hängt, das Nachhören aber
   * liefert — er nennt, was jetzt wieder abschickt, aus der Einstellung.
   */
  const nachhoerHinweis = (): string => {
    const e = cb.selbstSenden?.();
    const wort = (e?.wort ?? "").trim();
    const ms = Number(e?.stilleMs ?? 0);
    const stille = ms === STILLE_AUTO_MS ? "Stille (Auto)" : ms > 0 ? `${Math.round(ms / 1000)} s Stille` : "";
    const wege = [wort ? `„${wort}“` : "", stille].filter(Boolean);
    return wege.length > 0
      ? `${HAENGT_MITHOEREN_ANFANG}: ${wege.join(" oder ")} schickt ab.`
      : HAENGT_HINWEIS;
  };
  /*
   * Tickets 2512/2420 (23.09.2026): was die Stille-Entscheidung gesehen hat,
   * als EINE Zeile `[ptt-stille]` je Aufnahme. Nur Messung — siehe
   * stille-messung.ts. Je Fenster neu, und ein Fenster ist eine Aufnahme.
   */
  const stilleMessung = neueStilleMessung();
  /*
   * Ticket 2867 (01.10.2026): das Satzende-Signal `<end>` der Erkennung — nur
   * gemessen, es entscheidet nichts (ende-token.ts). Je Fenster, wie oben.
   */
  const endeMessung = neueEndeMessung();
  let aufnahmeLief = false;
  let stilleGemeldet = false;
  /**
   * Wie lange die Live-Mitschrift schon unverändert steht — `undefined` ohne Live-Text
   * (nativer Weg, noch kein Wort) und beim Nachhören: dort kommt der Text in Schüben, seine
   * Ruhe sagt nichts darüber, ob ein Laut ein Wort war (wie `textRuhtMs` für Jev unten).
   * EINE Fassung für `[pause-weiter]` und die Sprachgrenze.
   */
  const liveTextRuhtMs = (jetzt = performance.now()): number | undefined =>
    !nachhoer && letzterText !== "" ? jetzt - textSeit : undefined;
  /*
   * 07.10.2026 (Prüfbefund B1, Fall 05.10. 08:48:22): die Sprachgrenze der Anzeige gilt
   * nur im Auto-Modus, wenn der Server sie freigibt UND die Live-Mitschrift seit TEXTRUHE_AB_MS steht —
   * ein Laut, aus dem kein Wort wird, setzt die Stille nicht zurück (derselbe Gedanke wie
   * „Laut ohne neues Wort“, shared/stille-auto.ts). Bewegt sich der Text, gilt die alte
   * Grenze: ein leiser Ansatz vor dem Weitersprechen hält die Stille dann wie vorher an.
   */
  const sprachGilt = (): boolean => autoModus && sprachgrenzeGilt && (liveTextRuhtMs() ?? 0) >= TEXTRUHE_AB_MS;

  const viz = createMicViz(16, (still, ms, tonSeit, roh, grenze, laut, sprach) => {
    stillMs = ms;
    tonLaeuft = laut === true;
    const jetzt = performance.now();
    // Ticket 2668: nur ein wirklich gemessenes Bild zählt (roh null: der
    // Kontext schläft oder der Rechentakt steht; beim Stoppen gar keins).
    if (roh !== null && roh !== undefined) {
      if (pegelSeit === null || jetzt - letztesPegelBild > PEGEL_LUECKE_MS) pegelSeit = jetzt;
      letztesPegelBild = jetzt;
    }
    // 20.10.2026 (Notausgang, jetzt-senden.ts dauerlaerm): seit wann JEDES gemessene Bild bei DAUERLAERM_PEGEL liegt.
    // Ein Bild darunter oder ein ungemessenes beendet es — Rede hat Täler, Dauerlärm nicht.
    dauerLautSeit = roh !== null && roh !== undefined && roh >= DAUERLAERM_PEGEL ? (dauerLautSeit ?? jetzt) : null;
    /*
     * Tickets 2668/2697: Sprache ist derselbe anhaltende Ton, der die Stille
     * beendet (mic-visualizer.ts). Der eigene Ton beim Öffnen zählt dort gar
     * nicht erst (tonZaehltAb, TON_SCHONFRIST_MS).
     */
    if (tonSeit !== null) {
      tonBelegt = true;
      stimmeSeit ??= jetzt;
      // Ticket 2643 (26.09.2026 spät): Ton nach „nur Stille" — der Satz
      // stimmt nicht mehr und geht.
      if (stummGesagt && !stummWiderlegt) {
        stummWiderlegt = true;
        if (stummSatz !== null) nimmHinweisZurueck(stummSatz, stummVorher);
        einstellungenKnopf?.remove();
        einstellungenKnopf = null;
      }
    }
    merkeBild(stilleMessung, roh, ms, hatGesprochen(), grenze, sprach);
    /*
     * 03.10.2026 (Fantasiewörter): Pegel und Grenze dieses Bildes nur MERKEN,
     * dazu den Beginn der laufenden Stille — gerechnet wird in rausch-woerter.ts.
     * Nur ein gemessenes Bild in einer echten Stille gibt einen Beginn; während
     * eines lauten Stücks (die Stille steht) gibt es keinen.
     */
    pegelVerlauf.merke(jetzt, roh, grenze);
    if (typeof grenze === "number") letzteGrenze = grenze;
    stilleBeginn = roh !== null && roh !== undefined && ms > 0 && laut !== true ? jetzt - ms : null;
    // Ticket 2867: für `[ende-token]` — Pausen ab 1,5 s, nach denen laut Pegel weitergesprochen wurde.
    // Jarvis-Tempo (02.10.2026): nach einer Pause ab 1 s kommt wieder Ton → nur die Länge ins Journal, kein Inhalt.
    const pauseWeiter = merkePegel(endeMessung, ms, tonSeit !== null);
    // 05.10.2026: dazu, wie lange der Text da schon stand (nur mit Live-Text) — das Risiko von „Laut ohne neues Wort“.
    if (pauseWeiter !== null && jevMoeglich()) meldeSpur("pause-weiter", pauseWeiterZeile(pauseWeiter, liveTextRuhtMs(jetzt)));
    // 05.10.2026: anhaltender Ton, aus dem (noch) kein neues Wort wurde — nur gemessen (ende-token.ts, ohneWort…).
    merkeTonOhneWort(endeMessung, tonSeit !== null, tonBildVorher === null ? 0 : jetzt - tonBildVorher);
    tonBildVorher = jetzt;
    /*
     * ⚠️⚠️ 12.09.2026 — Ticket 2230, Martin: *„Wie kann kein Ton am Mikro
     * sein, dass es aber trotzdem aufgenommen wird? Das ist doch Bullshit."*
     *
     * `still` kommt vom separaten Pegel-Analyser (eigener AudioContext auf
     * der Spur der Aufnahme, erst nach einer Rettung auf einer Kopie — siehe
     * mic-visualizer.ts) — nicht vom Rekorder selbst. Hat die echte
     * Aufnahme (cb.hatEchtenTon) bereits Ton, ist die Warnung falsch,
     * unabhaengig davon, was die Kopie gerade misst.
     */
    const wirklichStill = still && !cb.hatEchtenTon?.();
    card.classList.toggle("kein-pegel", wirklichStill);
    /*
     * ⚠️⚠️ 20.09.2026 — HIER HAETTE EINE FLUT ENTSTEHEN KOENNEN, UND ZWAR
     *      DURCH MICH, am selben Tag.
     *
     *      `meldeMikroAnlass` schickt bei JEDEM Aufruf ein HTTP-POST. Bis
     *      pre.3078 meldete `onStille` nur beim WECHSEL — also einmal je
     *      Stille. Fuer das Absenden nach Stille (Ticket 2420) habe ich es
     *      auf „immer melden" umgestellt, weil dort der laufende Wert
     *      gebraucht wird. Damit haette diese Zeile bis zu sechzig Anfragen
     *      je Sekunde ausgeloest, solange die Karte offen und der Pegel
     *      still ist.
     *
     *      Eingetreten ist es nicht (null `ptt-kein-pegel`-Zeilen seit dem
     *      Bau) — es braucht die offene Karte UND 2,5 s ohne Pegel. Aber es
     *      lag offen da.
     *
     *      Die Meldung gehoert an den WECHSEL, der laufende Wert an die
     *      Entscheidung. Zwei Fragen, zwei Antworten.
     */
    if (wirklichStill !== warKeinPegel) {
      warKeinPegel = wirklichStill;
      // #2103: zählbar machen, wie oft die Warnung überhaupt erscheint.
      if (wirklichStill) meldeMikroAnlass("ptt-kein-pegel");
    }
  }, (e) => {
    /*
     * Ticket 2634 (26.09.2026): steht der Rechentakt der Seite, bekommen
     * Pegel, Stille und Mitschrift nichts — die Aufnahme läuft trotzdem.
     * Jede Stufe genau eine Zeile (die Wache begrenzt sie selbst). Gibt sie
     * auf, sagt die Karte es: sonst wartet man auf ein Absenden nach Stille,
     * das ohne Pegel nie kommen kann.
     */
    meldeMikroAnlass(`rechentakt-${e.stufe}`, taktZusatz("pegel", e));
    if (e.stufe === "tot") pegelAufgegeben = true;
    if (e.stufe === "tot" && card.dataset.state === "recording") {
      // Ticket 2672: hört der Server schon nach, geht Absenden per Wort und
      // Stille wieder — dann sagt der Satz das, nicht „bitte selbst tippen".
      zeigeHinweis(nachhoer ? nachhoerHinweis() : HAENGT_HINWEIS);
    }
  }, sprachGilt);
  viz.el.classList.add("pttm-viz");
  /** 07.10.2026: die Stille nach der alten Grenze in diesem Moment (`[ende-token] altMs=`) — wo die Stille nicht vom Pegel kommt, dieselbe wie `stilleJetzt`. */
  const stilleAltJetzt = (): number => (nachhoer && pegelGemessenMs() === null ? nachhoer.stilleMs : viz.stilleAltMs());
  /*
   * ⚠️ Der Pegel wird NACH der Mitschrift angehaengt (weiter unten). Er
   * stand bisher direkt unter dem Kopf und schob den Satz nach unten —
   * eine 60 Punkte hohe Zierleiste vor der einzigen Information.
   *
   * Der geteilte Visualizer bleibt unangetastet (dieselbe Fassung wie in
   * der Eingabeleiste, `dq-viz`). Aus den 16 Balken wird im Blatt eine
   * flache Linie, die mitläuft — nur ueber `.pttm-viz`, also ohne
   * Nebenwirkung auf die andere Stelle.
   */
  // 2026-08-01 (Ticket 1536): Den AudioContext HIER wecken, nicht erst beim
  // ersten Pegel. Diese Zeile laeuft noch in der Fingergeste — openPttModal
  // wird direkt aus startPttRecording gerufen, im selben Zug wie hapticTap
  // und unlockAudio. startLevels() dagegen kommt erst mit
  // setStatus("recording"), also nach dem asynchronen Mikro-Start und damit
  // ausserhalb der Geste; ein dort angelegter Kontext bleibt auf iOS
  // angehalten und der Analyser liefert nur Nullen.
  viz.prepare();

  // #533 (2026-07-24): echter Mikro-Pegel auf die 16 Balken. STRENG NUR
  // LESEND — eigener AudioContext + AnalyserNode auf der Spur der Aufnahme
  // (03.10.2026 nachgesehen: KEINE Kopie, die gibt es erst nach einer
  // Rettung der Rechentakt-Wache); die Aufnahme-Pipeline bleibt unberuehrt
  // (#311-Saga). Fail-open: ohne Pegel sagt die Anzeige „Pegel nicht
  // messbar“ (pegel-anzeige.ts) — eine Ersatz-Animation gibt es nicht.
  // Der Pegel kommt vom gemeinsamen Visualizer. Das Mikro startet asynchron —
  // solange getLevelTrack() noch nichts liefert, wird es erneut versucht.
  let levelRaf = 0;
  let pegelTimer = 0;
  /*
   * pre.3623: Jeder native Lauf hat eine Nummer. Eine Pegelantwort, die erst
   * nach stopLevels ankommt, gehört zum alten Lauf und füttert nichts mehr —
   * sonst zählte sie nach dem Stoppen als Bild (Stille, `[ptt-stille]`) und
   * hob einen Balken der ruhenden Anzeige.
   */
  let pegelLauf = 0;
  /*
   * Ticket 2420 — von selbst absenden.
   *
   * ⚠️ Ein eigener Takt (250 ms), NICHT der Bildschirm-Takt: die Entscheidung
   *    haengt an Zeitspannen, nicht an Bildern. Und sie wird HOECHSTENS
   *    EINMAL je Aufnahme getroffen — ein zweites Absenden waere ein zweiter
   *    Besitzer neben dem Knopf.
   */
  const stoppeSelbstSenden = (): void => {
    if (sendeTimer) window.clearInterval(sendeTimer);
    sendeTimer = 0;
  };

  /**
   * Die Lage für `autoPruefung` und `autoRestMs` — EINE Stelle für den Takt und den Messfühler `autoStand`.
   * `text`: der Text, über den Jev urteilt (urteilsText).
   */
  // 17.10.2026: die Abkürzung gilt nur nach einer einfachen Gesprächsfrage — nie, wenn eine Entscheidung wartet (M2).
  const sofortZusage = cb.nachFrage === true && cb.entscheidungOffen === false;
  const autoLageVon = (lage: SendeLage, text: string): AutoLage => ({
    text,
    stillMs: lage.stillMs,
    gesprochen: lage.gesprochen,
    urteil: autoUrteil,
    fragtSeitMs: autoFrage?.text === text ? performance.now() - autoFrage.seit : null,
    // 05.10.2026: ein Laut ohne neues Wort setzt Jevs Uhr nicht zurück — die Ruhe des Textes zählt mit.
    // Nur mit Live-Mitschrift: beim Nachhören ist `textRuhtMs` die Stille der letzten Probe, keine Ruhe des Textes.
    textRuhtMs: nachhoer ? undefined : lage.textRuhtMs,
    // 12.10.2026: früh fragen (geschlossener Satz) nur mit Ja des Servers zur letzten Antwort.
    fein: feinZuletzt,
    // 15.10.2026: kurze Zusage nach 1,2 s nur mit Ja des Servers zur letzten Antwort (oder im Urteil selbst).
    zusage: zusageZuletzt,
    // 15.10.2026: Wortstille vor dem Urteil für genau diesen Text nur mit Ja des Servers zur letzten Antwort.
    wortruhe: wortruheZuletzt,
    // 17.10.2026: das Mikro ging nach einer Frage auf — eine kurze Zusage geht sofort hinaus.
    frageOffen: sofortZusage,
  });

  /** 20.10.2026: wartet die Bremse, sagt es die Karte (wie „Verwerfen“); danach kommt die Zeile von vorher zurück. */
  let bremseZeigt: string | null = null;
  const zeigeBremse = (grad: SatzOffen | null | false): void => {
    // `false`: die Bremse wartet nicht. Sonst der Grad — „unfertig“ steht nur bei `offen` in der Zeile.
    const text = grad === false ? null : bremsWarteHinweis(grad);
    if (text === bremseZeigt) return;
    if (bremseZeigt === null) bremseVorher = hinweisSatz;
    const vorher = bremseZeigt;
    bremseZeigt = text;
    if (text !== null) {
      zeigeHinweis(text);
    } else if (vorher !== null) {
      nimmHinweisZurueck(vorher, bremseVorher);
    }
  };

  /**
   * EIN Durchgang der Entscheidung „von selbst absenden“. Der 250-ms-Takt ruft ihn je Takt; seit 12.10.2026
   * ruft ihn auch Jevs Antwort sofort (`ausAntwort`, siehe frageAuto), wenn der Server den Feinschliff meldet.
   * Dieselbe Funktion für beide, also dieselbe Regel — und höchstens EIN Absenden je Aufnahme:
   * `schonGesendet` wird vor dem Absenden gesetzt und der Takt gestoppt, ein zweiter Aufruf (Takt oder
   * Antwort, gleich in welcher Reihenfolge) kehrt an der ersten Zeile zurück.
   * `ausAntwort` zählt keinen Takt (`stilleMessung.takte`) und fragt nie neu: eine neue Frage stellt erst der Takt.
   */
  const selbstSendenPruefen = (ausAntwort: boolean): void => {
    if (schonGesendet) return;
    const e = cb.selbstSenden?.();
    // Tickets 2512/2420: welche Schwelle dieser Takt geladen hat — die
    // EINZIGE Quelle für `schwelle` in der Zeile (fragte nie ein Takt,
    // steht dort „-“).
    if (!ausAntwort) stilleMessung.takte++;
    stilleMessung.schwelleMs = Number(e?.stilleMs ?? 0);
    // 07.10.2026: die Sprachgrenze gilt nur in Auto — wer mitten in der Aufnahme auf 2/3 s wechselt, hat den alten Weg.
    autoModus = stilleMessung.schwelleMs === STILLE_AUTO_MS;
    if (!e) return;
    const lage = sendeLage();
    /*
     * Ticket 2763: bei „Auto" sagt autoSchritt (shared/stille-auto.ts), ob
     * abgeschickt, Jev gefragt oder gewartet wird. Gefragt wird hier, die
     * Antwort kommt in einem späteren Takt an.
     */
    let einst = e;
    let autoGrund: ReturnType<typeof autoPruefung> | null = null;
    if (Number(e.stilleMs) === STILLE_AUTO_MS) {
      // 03.10.2026: Fantasiewörter am Ende zählen fürs Urteil nicht (urteilsText).
      const rausch = rauschJetzt(lage.text);
      const text = urteilsText(lage.text, rausch);
      // 07.10.2026: eine neue Pause zählt ihre Fragen neu (nur für die Zeile des Servers). 12.10.2026: ab der frühesten Frage.
      if (lage.stillMs < (feinZuletzt ? AUTO_FRAGE_FRUEHESTENS_MS : AUTO_FRAGE_AB_MS)) {
        pauseFragen = 0;
        pauseLetzterText = null;
      }
      /*
       * Ticket 2867: JEDER Takt rechnet neu. Das Urteil bleibt (Jev wird je
       * Text einmal gefragt), die Stille wächst — und mit ihr sinkt die
       * Schwelle (shared/stille-auto.ts, autoSchwelle). Nach „warten“ läuft
       * dieser Takt also weiter, bis die nächste Stufe greift.
       */
      autoGrund = autoPruefung(autoLageVon(lage, text));
      if (autoGrund.schritt === "fragen" && !ausAntwort) frageAuto(text, lage.text.trim(), rausch);
      // 20.10.2026: die Zusage („Ja“ nach einer Frage) braucht nur eine kurze Ruhe des Textes (jetzt-senden.ts, textRuhtGenug).
      einst = { ...e, autoSenden: autoGrund.schritt === "senden", autoZusage: autoGrund.grund === "zusage" };
    }
    const grund = sendeGrund(lage, einst);
    const amEnde = endwortAmEnde(lage, e);
    /*
     * Ticket 2674: „Verwerfen" wartet zwei Sekunden auf Ruhe. So lange
     * sagt die Karte, was gleich geschieht — und wie man es verhindert.
     */
    const wartet = !grund && amEnde?.handlung === "verwerfen";
    if (wartet !== verwerfenWartet) {
      verwerfenWartet = wartet;
      if (wartet) {
        verwerfenVorher = hinweisSatz;
        zeigeHinweis(VERWERFEN_HINWEIS);
      } else {
        nimmHinweisZurueck(VERWERFEN_HINWEIS, verwerfenVorher);
      }
    }
    /*
     * 20.10.2026 (Martin 22:00: „… Vor allem wenn das Swoopband“ ging mitten im Satz hinaus): die Vollständigkeits-Bremse.
     * Will die STILLE senden (jeder Weg: fest, Auto mit Jev, Obergrenze, Rückfall, Wortstille, Notausgang), prüft sie das
     * Ende der Mitschrift: offen → länger warten, danach ins Eingabefeld statt senden; ohne Satzende → länger warten.
     * Wort, Knopf und Taste prüfen hier nicht — Martins Hand geht immer.
     */
    const notausgang = grund === "stille" && stilleWeg(lage, einst) === "notausgang";
    // Fantasiewörter in der Stille (rausch-woerter.ts) sind keine Rede: geprüft wird das Ende ohne sie — in jedem Modus.
    const bremsText = grund === "stille" ? ohneEndWoerter(lage.text, rauschJetzt(lage.text).k) : "";
    const brems = grund === "stille" ? bremse.pruefe(bremsText, lage.stillMs, performance.now(), notausgang) : null;
    if (brems === null) bremse.vergiss();
    zeigeBremse(bremse.wartet() ? bremse.grad() : false);
    if (brems === "warten") return;
    if (!grund) return;
    schonGesendet = true;
    stoppeSelbstSenden();
    if (brems === "zuruecklegen") {
      meldeSpur("ptt", `bremse unvollstaendig stillMs=${Math.round(lage.stillMs)} notausgang=${notausgang ? "ja" : "nein"}`);
      cb.onSelbstSenden?.("unvollstaendig");
      return;
    }
    // 20.10.2026: über den Notausgang (Dauerlärm)? Dann sagt es die Zeile `[ptt-ende] senden` (ruheStand).
    if (notausgang) notausgangMs = Math.round(lage.dauerLautMs ?? 0);
    // 17.10.2026: das Ja ist angekommen — sofort sichtbar (Ton und Vibration folgen mit der Quittung, sobald der Server es annahm).
    if (grund === "stille" && autoGrund?.schritt === "senden" && autoGrund.grund === "zusage" && sofortZusage) {
      zeigeHinweis(ZUSAGE_GESENDET_HINWEIS[zusageAntwort(lage.text) ?? "neutral"]);
    }
    // Ticket 2867: für die Zeile [ende-token] — wodurch, in welcher Stufe, bei welcher Stille.
    if (grund === "stille" && autoGrund?.schritt === "senden" && autoGrund.grund) {
      merkeAutoSenden(endeMessung, autoGrund.grund, autoGrund.stufe, lage.stillMs, lage.textRuhtMs, autoGrund.jevStillMs, stilleAltJetzt(), urteilMessung(urteilsText(lage.text)));
    }
    if ((grund === "wort" || grund === "verwerfen") && amEnde) {
      cb.onSelbstSenden?.(grund, { fuerSich: amEnde.fuerSich, text: amEnde.text });
    } else {
      cb.onSelbstSenden?.(grund);
    }
  };

  /**
   * 12.10.2026 (Messfühler, nur Zahlen): was zu Jevs Urteil für genau diesen Text zu sagen ist — ab welcher Stille
   * es nach der Regel hätte senden dürfen (`fruehMs`, wie `[stille-auto] fruehMs=`) und bei welcher Stille die
   * Frage gestellt wurde (`frageMs`). Ohne Urteil für diesen Text: beides `null`.
   */
  const urteilMessung = (text: string): { fruehMs: number | null; frageMs: number | null } => {
    const u = autoUrteil;
    if (u?.text !== text.trim()) return { fruehMs: null, frageMs: null };
    const frueh =
      u.fertig === null
        ? null
        : autoSendeAbMs(u.fertig, u.stufen === true, u.satzzeichen === true ? satzEnde(u.text) : null, tempoLesen(u.tempo), urteilEnde(u));
    return { fruehMs: frueh, frageMs: urteilFrageMs };
  };

  const starteSelbstSenden = (): void => {
    stoppeSelbstSenden();
    schonGesendet = false;
    tonBelegt = false;
    nachhoerHoerte = false;
    liveWortGehoert = false;
    liveAntwortKam = false;
    nachhoerTextKam = false;
    wortlosEntschieden = false;
    stimmeSeit = null;
    stillMs = 0;
    letzterText = "";
    textSeit = performance.now();
    textVerlauf = neuerTextVerlauf();
    alleWoerter = [];
    rauschFest = null;
    autoUrteil = null;
    autoFrage = null;
    urteilFrageMs = null;
    pauseFragen = 0;
    pauseLetzterText = null;
    sprachgrenzeGilt = false;
    autoModus = false;
    dauerLautSeit = null;
    notausgangMs = null;
    bremse.vergiss();
    zeigeBremse(false);
    zeigeJev();
    if (!cb.onSelbstSenden || !cb.selbstSenden) return;
    sendeTimer = window.setInterval(() => selbstSendenPruefen(false), 250);
  };

  const stopLevels = (): void => {
    if (levelRaf) cancelAnimationFrame(levelRaf);
    levelRaf = 0;
    if (pegelTimer) window.clearInterval(pegelTimer);
    pegelTimer = 0;
    pegelLauf++;
    viz.stop();
  };
  const startLevels = (): void => {
    if (!cb.getLevelTrack) return;
    const track = cb.getLevelTrack();
    if (!track) {
      // Nativer Lauf: keine Tonspur, aber ein Pegel — von außen füttern.
      const nativ = cb.pegelNativ?.();
      if (nativ && !pegelTimer) {
        stilleMessung.quelle = "nativ";
        viz.el.hidden = false;
        const lauf = pegelLauf;
        pegelTimer = window.setInterval(() => {
          const p = cb.pegelNativ?.();
          if (!p) return;
          void p.then((v) => {
            if (lauf === pegelLauf) viz.pushLevel(v);
          }).catch(() => undefined);
        }, 100);
        return;
      }
      levelRaf = requestAnimationFrame(startLevels);
      return;
    }
    stilleMessung.quelle = "browser";
    viz.start(track);
  };

  /*
   * 24.09.2026 (Ticket 2518): Tauscht die Sprechtaste mitten im Druck ihre
   * tote Leitung gegen eine frische, sagt die Karte es — in der Hinweiszeile,
   * die schon `role="status"` trägt, also auch vorgelesen wird. Und der Pegel
   * zieht auf die neue Spur um; auf der alten stand er ohnehin still.
   */
  const leitungAbmelden = hoereAufLeitungsWechsel(() => {
    // Erst der Zustand: wer während des Wechsels losließ, sendet schon —
    // „sprich weiter“ wäre dann die falsche Aufforderung.
    if (card.dataset.state !== "recording") return;
    zeigeHinweis("Mikro neu verbunden – sprich weiter");
    // Ein noch wartender Versuch würde sonst eine zweite Schleife bilden.
    if (levelRaf) cancelAnimationFrame(levelRaf);
    levelRaf = 0;
    startLevels();
  });

  /*
   * 26.09.2026 (Ticket 2643): Die Sprechtaste hat eine stumme Quelle schon
   * einmal getauscht, und auch die neue liefert nur Stille. Ein zweiter
   * Tausch wird nicht versucht — hier steht, was jetzt hilft. Gleiche Zeile,
   * gleiche Rolle wie beim Tausch, damit es auch vorgelesen wird.
   *
   * 26.09.2026 abends: „Loslassen und neu drücken" half Konto 23 nie — Web-
   * UND nativer Weg lieferten −91 dB, reine Nullen. So etwas liefert das
   * Gerät, wenn die App das Mikrofon nicht benutzen darf. Darum sagt der
   * Satz jetzt, wo man es einschaltet, genau einmal — und meldet den
   * Befund an app.ts.
   *
   * 26.09.2026 spät (dritte Runde): Die Aufnahme geht trotzdem zur
   * Erkennung (der Server verwirft echte Stille selbst). Kommt später doch
   * ein Laut, nimmt die Karte den Satz zurück (siehe den Pegel oben), und
   * nach dem Ende steht er nicht im Verlauf (`stummBisher`).
   */
  const weg = (): string => (cb.inApp === true ? "app" : "browser");
  /**
   * Ticket 2643 (29.09.2026): der Weg zum Recht, direkt unter dem Satz —
   * nur, wenn der Satz in die Einstellungen führt und die App sie öffnen kann.
   */
  const zeigeEinstellungenKnopf = (): void => {
    if (einstellungenKnopf || !hinweisEl || !cb.oeffneEinstellungen) return;
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "pttm-btn pttm-btn--ghost pttm-anlass-knopf";
    knopf.textContent = "iPhone-Einstellungen öffnen";
    knopf.addEventListener("click", () => {
      void cb.oeffneEinstellungen?.().then((ok) =>
        meldeMikroAnlass("einstellungen-geoeffnet", `ok=${ok ? "ja" : "nein"}`),
      );
    });
    hinweisEl.after(knopf);
    einstellungenKnopf = knopf;
  };
  /** Ticket 2643: das Recht lesen und aus dem Satz den richtigen machen. */
  const verfeinereStumm = async (): Promise<void> => {
    if (!cb.mikroRecht) return;
    const gesagt = stummSatz;
    const recht: MikroRecht = await cb.mikroRecht().catch(() => "unbekannt" as const);
    meldeMikroAnlass("mikro-recht", `recht=${recht} weg=${weg()}`);
    // Zurückgenommen (Ton kam) oder von etwas anderem übernommen: nichts tun.
    if (stummWiderlegt || gesagt === null || hinweisSatz !== gesagt) return;
    stummSatz = stummHinweisFuer(cb.inApp === true, recht);
    zeigeHinweis(stummSatz);
    if (brauchtEinstellungen(recht)) zeigeEinstellungenKnopf();
  };
  const sageStumm = (): void => {
    stummGesagt = true;
    stummVorher = hinweisSatz;
    stummSatz = stummHinweis(cb.inApp === true);
    zeigeHinweis(stummSatz);
    meldeMikroAnlass("hinweis-stummes-mikro", `weg=${weg()}`);
    cb.onStummeQuelle?.();
    void verfeinereStumm();
  };
  const stummAbmelden = hoereAufStummeQuelle(() => {
    if (stummGesagt || stummAusstehend) return;
    const zustand = card.dataset.state ?? "?";
    if (zustand === "recording") {
      sageStumm();
      return;
    }
    // Die Karte öffnet noch das Mikro: der Satz kommt mit dem Aufnahmebeginn.
    if (zustand === "starting") {
      stummAusstehend = true;
      meldeMikroAnlass("hinweis-stummes-mikro-wartet", `zustand=${zustand} weg=${weg()}`);
      return;
    }
    // Schon beim Senden: nichts mehr sagen — aber belegen, dass es kam.
    meldeMikroAnlass("hinweis-stummes-mikro-aus", `zustand=${zustand} weg=${weg()}`);
  });

  /**
   * Tickets 2512/2420: die Zeile `[ptt-stille]` — genau einmal je Aufnahme,
   * bei jedem Ende (Senden, Selbst-Senden, Verwerfen). Nichts, wenn die
   * Aufnahme nie lief (Start gescheitert).
   */
  const meldeStilleEinmal = (): void => {
    if (!aufnahmeLief || stilleGemeldet) return;
    stilleGemeldet = true;
    meldeSpur(
      "ptt-stille",
      stilleZeile(stilleMessung, {
        gesprochen: hatGesprochen(),
        dauerMs: Date.now() - startedAt,
      }),
    );
    /*
     * Ticket 2867: dazu EINE Zeile [ende-token] — nur Zahlen, nur Konto 1 (wie
     * Jev selbst). Jevs Urteil zählt nur, wenn es für den Schlusstext galt.
     */
    if (jevMoeglich()) {
      const u = autoUrteil;
      const gilt = u !== null && u.text === urteilsText(letzterText);
      // 02.10.2026: dazu die ART des Satzendes beim Abschicken (nie der Text) und ob die Satzzeichen-Regel galt.
      const schluss = letzterText.trim();
      meldeSpur(
        "ende-token",
        endeZeile(
          endeMessung,
          gilt
            ? { fertig: u.fertig, stufen: u.stufen === true, satzzeichen: u.satzzeichen === true, textruhe: u.textruhe === true }
            : null,
          schluss ? satzEnde(schluss) : null,
        ),
      );
    }
  };

  /*
   * ── Mitgeschriebener Text ────────────────────────────────────────────
   *
   * 2026-08-28. Die Karte ist die richtige Buehne dafuer, und zwar aus einem
   * gemessenen Grund: Sie sitzt OBEN (`top: calc(var(--topbar-h) + 10px)`),
   * waehrend der Daumen beim Halten unten auf dem Knopf liegt. Jede Flaeche
   * ueber dem Dock haette ausserdem an der Regel gehangen, die beim Tippen
   * das ganze Dock ausblendet.
   *
   * Sie erscheint erst, wenn wirklich Worte da sind. Ohne Erkennung bleibt
   * die Karte genau so, wie sie vorher war.
   */
  const mitschrift = document.createElement("div");
  mitschrift.className = "pttm-live";
  mitschrift.hidden = true;
  card.appendChild(mitschrift);
  card.appendChild(jevEl);
  /*
   * Ticket 2672: nachgereichter Text trägt ein kleines Wort davor. Er kommt
   * in Schüben (alle paar Sekunden) statt Wort für Wort — ohne Kennzeichnung
   * sähe das aus wie eine hängende Mitschrift.
   */
  const marke = document.createElement("span");
  marke.className = "pttm-live-marke";
  marke.textContent = "nachgereicht";
  /*
   * Ticket 2607: ein Hinweis steht an derselben Stelle, aber leise — groß
   * gesetzt las Martin „Ich höre zu" als das, was die Karte verstanden hat.
   * Als Statusmeldung liest ihn auch ein Bildschirmleser vor.
   */
  const zeigeMitschrift = (t: string, art: "live" | "nachgereicht" | "hinweis"): void => {
    /*
     * Ticket 2741: ausgeblendet heißt ausgeblendet — auch der leise Hinweis,
     * warum kein Text mitläuft (Ticket 2607), denn er erklärt nur die
     * Mitschrift. Was entschieden wird (letzterText, textVerlauf), steht
     * davor in setLiveText und bleibt unberührt.
     */
    if (ohneMitschrift) {
      mitschrift.hidden = true;
      return;
    }
    if (art === "nachgereicht" && t) mitschrift.replaceChildren(marke, document.createTextNode(t));
    else mitschrift.textContent = t;
    mitschrift.hidden = t.length === 0;
    const hinweis = art === "hinweis" && t.length > 0;
    mitschrift.classList.toggle("is-hinweis", hinweis);
    if (hinweis) mitschrift.setAttribute("role", "status");
    else mitschrift.removeAttribute("role");
    card.classList.toggle("hat-mitschrift", t.length > 0 && !hinweis);
    // Immer das Ende zeigen — beim Sprechen waechst der Text nach unten.
    // Immer, auch während der Daumen die Sprechtaste hält: die Mitschrift ist 3 Zeilen hoch und lässt sich
    // nicht scrollen (overflow: hidden) — hier gibt es keinen Leser, den ein Nachziehen stören könnte.
    // Bis pre.3608 wartete sie auf „Ruhe“, und bei gehaltener Taste blieben die neuesten Wörter unsichtbar.
    schreibeScrollTop(mitschrift, mitschrift.scrollHeight - mitschrift.clientHeight, "mitschrift");
    // Läuft der Anfang über den Rand, läuft er nach oben sanft aus (style.css, .ueberlaeuft).
    mitschrift.classList.toggle("ueberlaeuft", mitschrift.scrollHeight - mitschrift.clientHeight > 1);
  };
  card.appendChild(viz.el);
  const keinPegel = document.createElement("div");
  keinPegel.className = "pttm-kein-pegel";
  keinPegel.textContent =
    "Kein Ton am Mikro — es wird trotzdem aufgenommen und danach erkannt.";
  card.appendChild(keinPegel);

  // ── Gesten-Hinweis (nur im Halte-Modus sichtbar) ──────────────────────
  const hint = document.createElement("div");
  hint.className = "pttm-hint";
  hint.textContent =
    "Loslassen sendet · hochziehen verriegelt · links abbrechen";
  card.appendChild(hint);

  // ── Aktionen (nur verriegelt sichtbar) ────────────────────────────────
  const actions = document.createElement("div");
  actions.className = "pttm-actions";
  /*
   * ── Der Entwurf „Der Zettel" (2026-08-29) ────────────────────────────
   *
   * Martin: *„bitte die Chatvorschau auch komplett überarbeiten,
   * designtechnisch."* — und nach zwei Entwürfen: *„A"*.
   *
   * ## Was gemessen falsch war
   *
   *   Timer „0:00"            27 px   ← der GRÖSSTE Text der Karte
   *   der erkannte Satz       13 px   ← halb so groß
   *   Verwerfen/Textfeld/Senden  104/104/102 pt
   *
   * Die Hierarchie stand auf dem Kopf: die Zahl, die niemand braucht, war
   * doppelt so groß wie der Satz, den man prüfen muss. Und Wegwerfen wog
   * genauso viel wie Senden — drei gleich große Knöpfe nebeneinander, kein
   * sicheres Daumenziel darunter.
   *
   * ⚠️ Deshalb zwei Ebenen statt einer Reihe: Senden bekommt die volle
   * Breite, die Nebenwege werden Wörter darunter. Ein Weg, der etwas
   * WEGWIRFT, darf nicht so leicht zu treffen sein wie der richtige.
   */
  const nebenwege = document.createElement("div");
  nebenwege.className = "pttm-nebenwege";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "pttm-btn pttm-btn--ghost";
  cancelBtn.setAttribute("aria-label", "Aufnahme verwerfen");
  cancelBtn.appendChild(
    svgIcon("M9 3h6v2H9zM4 6h16M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14"),
  );
  cancelBtn.appendChild(textSpan("Verwerfen"));
  cancelBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    druckVerwerfen("knopf");
  });
  nebenwege.appendChild(cancelBtn);
  // 2026-07-29: Dritter Weg — Text übernehmen statt senden. Steht bewusst in
  // der Mitte: links wegwerfen, rechts abschicken, dazwischen der ruhige Weg.
  if (cb.onToText) {
    const toTextBtn = document.createElement("button");
    toTextBtn.type = "button";
    toTextBtn.className = "pttm-btn pttm-btn--totext";
    toTextBtn.setAttribute(
      "aria-label",
      "Gesprochenes ins Textfeld übernehmen, ohne zu senden",
    );
    // Stift-Symbol: bearbeiten, nicht abschicken.
    toTextBtn.appendChild(
      svgIcon("M4 20h4L18.5 9.5a2.1 2.1 0 10-3-3L5 17v3zM13.5 6.5l3 3"),
    );
    toTextBtn.appendChild(textSpan("Ins Textfeld"));
    toTextBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      cb.onToText?.();
    });
    nebenwege.appendChild(toTextBtn);
  }
  // Ticket 781: Umschreiben. Steht neben "Ins Textfeld", weil beide am
  // Entwurf arbeiten statt zu senden — nur schreibt dieser den vorhandenen
  // Text um, statt anzuhaengen.
  if (cb.onRewrite) {
    const rewriteBtn = document.createElement("button");
    rewriteBtn.type = "button";
    rewriteBtn.className = "pttm-btn pttm-btn--rewrite";
    rewriteBtn.setAttribute(
      "aria-label",
      "Gesprochenes als Anweisung nehmen und den Entwurf umschreiben",
    );
    // Kreispfeil: dasselbe nochmal, anders.
    rewriteBtn.appendChild(
      svgIcon("M20 12a8 8 0 11-2.34-5.66M20 4v4h-4"),
    );
    rewriteBtn.appendChild(textSpan("Umschreiben"));
    rewriteBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      cb.onRewrite?.();
    });
    nebenwege.appendChild(rewriteBtn);
  }
  const stopBtn = document.createElement("button");
  stopBtn.type = "button";
  stopBtn.className = "pttm-btn pttm-btn--send";
  const hauptWort = cb.hauptLabel ?? "Senden";
  stopBtn.setAttribute(
    "aria-label",
    cb.hauptLabel
      ? `Aufnahme stoppen und ${hauptWort.toLowerCase()}`
      : "Aufnahme stoppen und senden",
  );
  stopBtn.appendChild(svgIcon("M12 19V5M5 12l7-7 7 7"));
  const hauptText = textSpan(hauptWort);
  stopBtn.appendChild(hauptText);
  /*
   * Ticket 2634 (26.09.2026): Martin musste „mehrfach draufdrücken". Im
   * Protokoll stand nur EIN Senden, und das ging sofort durch — ob die
   * früheren Drücke hier ankamen, war nicht zu sehen. Jetzt meldet sich jeder
   * weitere Druck mit dem Zustand der Karte.
   */
  let sendeDruecke = 0;
  stopBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    sendeDruecke++;
    if (sendeDruecke > 1) {
      meldeMikroAnlass("ptt-senden-nochmal", `druck=${sendeDruecke} zustand=${card.dataset.state ?? "-"}`);
    }
    cb.onStop();
  });
  // ⚠️ Senden ZUERST anhaengen: es steht oben und traegt die volle Breite.
  actions.appendChild(stopBtn);
  actions.appendChild(nebenwege);
  /*
   * ⚠️ 21.10.2026 (Martin: „Ja/Nein aus dem Modal weg … das ist verwirrend“): KEINE Ja/Nein-Knöpfe im Aufnahme-Fenster. Er kann nicht
   *    gleichzeitig sprechen und tippen; das Karten-Ja hat EINEN Ort — die Karte „Offene Frage“ im Verlauf (lib/frage-knoepfe.ts).
   *    Bis pre.3939 bildete hier ein Platz (lib/ptt-frage-slot.ts, 18.10.2026) das offene Paar ab — entfallen.
   */
  card.appendChild(actions);

  /*
   * ── Der Ausweg in „Sende …“ (30.09.2026, Ticket 2634) ─────────────────
   *
   * Martin, 20:04 und 22:05: die Karte blieb auf „Sende …“ stehen, „ich kann
   * es auch nicht mehr wegklicken, ich muss alles neu starten“. In diesem
   * Zustand gab es KEINEN Knopf (Verwerfen und Ins Textfeld sind aus, die
   * Hülle fängt keinen Tipp). Dabei ist die Aufnahme dann beim Server — es
   * gibt nichts mehr zu verwerfen, nur noch die Karte wegzunehmen.
   *
   * ⚠️ Der Knopf ist SOFORT da, nicht erst nach einer Wartezeit: wer hängt,
   *    weiß nicht, wie lange er hängt. Und er bricht nichts ab und sendet
   *    nichts neu — er blendet nur die Karte aus; die Zeile darunter sagt,
   *    wo die Antwort dann steht.
   * ⚠️ `hidden` statt nur CSS: der Zustand ist dann auch ohne Stylesheet
   *    lesbar (Hilfstechnik, Tests).
   */
  const ausweg = document.createElement("div");
  ausweg.className = "pttm-ausweg";
  ausweg.hidden = true;
  const ausblendenBtn = document.createElement("button");
  ausblendenBtn.type = "button";
  ausblendenBtn.className = "pttm-btn pttm-btn--ausweg";
  ausblendenBtn.setAttribute("aria-label", "Karte ausblenden – die Antwort kommt im Verlauf");
  // Pfeil nach oben: die Karte sitzt oben und zieht sich dorthin zurück.
  ausblendenBtn.appendChild(svgIcon("M6 15l6-6 6 6"));
  ausblendenBtn.appendChild(textSpan("Ausblenden"));
  ausblendenBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    ausblenden();
  });
  const ausblendenHinweis = document.createElement("p");
  ausblendenHinweis.className = "pttm-ausweg-hinweis";
  ausblendenHinweis.textContent = AUSBLENDEN_HINWEIS;
  ausweg.appendChild(ausblendenBtn);
  ausweg.appendChild(ausblendenHinweis);
  card.appendChild(ausweg);

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // Timer
  const startedAt = Date.now();
  /*
   * Ticket 2263 (14.09.2026, Martin früh): "Beim Sprechen über Push-to-Talk
   * endet die Aufnahme nach rund 5 Minuten hart und ohne jede Vorwarnung."
   * Dasselbe Muster wie .voice-live-timer.timer-refresh-soon (Pre.769): ab
   * PTT_WARNUNG_VOR_MS vor Schluss pulsiert der Timer dezent in Amber —
   * lautloser Heads-up, kein Ton, kein Rot/Alarm.
   */
  let gewarnt = false;
  /** Ticket 2618: der zuletzt angekündigte Teil (1 = noch keiner). */
  let angekuendigt = 1;
  /** Wann die Aufnahme wirklich lief (setStatus „recording“) — null vorher. */
  let aufnahmeSeit: number | null = null;
  let leerAbgebrochen = false;
  /** Seit wann die Seite verdeckt ist, während dieses Fenster offen ist. */
  let verdecktSeit: number | null =
    typeof document !== "undefined" && document.visibilityState === "hidden" ? Date.now() : null;
  /** Einmal entscheiden — im Takt (verdeckt läuft er ~1×/s) und beim Zurückkommen. */
  const pruefeVerdeckt = (): boolean => {
    if (leerAbgebrochen || aufnahmeSeit === null) return false;
    // ⚠️ Nachprüfung 01.10.2026 (Ticket 2634): In „Sende …“ gibt es nichts mehr zu verwerfen — die Aufnahme
    // ist beim Server. Sonst meldete die Taschen-Regel nach 60 s Hintergrund „verworfen“ und gab die
    // Sprechtaste frei, während der Zug noch lief.
    if (card.dataset.state === "sending") return false;
    if (!verdecktVerwerfen({ verdecktSeitMs: verdecktSeit === null ? null : Date.now() - verdecktSeit })) {
      return false;
    }
    leerAbgebrochen = true;
    meldeMikroAnlass("ptt-verdeckt-verworfen", `nach=${Math.round((Date.now() - (verdecktSeit ?? Date.now())) / 1000)}s`);
    cb.onCancel("verdeckt", "timer");
    return true;
  };
  const onSichtbarkeit = (): void => {
    if (document.visibilityState === "hidden") {
      verdecktSeit ??= Date.now();
      return;
    }
    // Zurück: zuerst entscheiden, ob die Tasche zu lang war — erst dann zählt
    // die Seite wieder als offen (sonst schickte das Absenden nach Stille).
    if (pruefeVerdeckt()) return;
    verdecktSeit = null;
  };
  document.addEventListener("visibilitychange", onSichtbarkeit);
  /**
   * Ticket 2668: das Nachlausch-Fenster, je Takt. Gesprochen → bleibt offen;
   * still über das ganze Fenster → zu (true); im Stillstand einmal melden,
   * warum es offen bleibt.
   */
  const pruefeNachlauschen = (): boolean => {
    if (nachlausch !== "an" || leerAbgebrochen) return false;
    const offen = aufnahmeSeit === null ? null : Date.now() - aufnahmeSeit;
    const gemessen = pegelGemessenMs();
    const fenster = cb.nachlauschMs ?? 0;
    if (hatGesprochen()) {
      nachlausch = "vorbei";
      meldeSpur("ptt", `nachlauschen-bleibt grund=gesprochen nach=${offen ?? 0}ms`);
      nimmHinweisZurueck(NACHLAUSCH_HINWEIS, nachlauschVorher);
      return false;
    }
    if (nachlauschenZu({ fensterMs: fenster, offenSeitMs: offen, gesprochen: false, gemessenMs: gemessen, tonLaeuft })) {
      nachlausch = "vorbei";
      leerAbgebrochen = true;
      /*
       * Nachbesserung (29.09.2026): lag in der Schonfrist anhaltender Ton,
       * kann es ein Wort gewesen sein (der eigene Ton oder ein „Ja“ bei
       * 0,7 s — am Pegel nicht zu trennen, viz.tonInSchonfrist). Dann geht
       * die Aufnahme wie nach Stille hinaus, statt verworfen zu werden: der
       * Server verwirft Stummes, und leer schließt das Fenster still
       * (nachlauschOhneSprache → pttLeerStill in app.ts). Nie geht Rede
       * verloren; im Zweifel eine Sendung zu viel.
       */
      const vorbehalt = viz.tonInSchonfrist();
      meldeSpur(
        "ptt",
        `nachlauschen-zu nach=${offen ?? 0}ms gemessen=${Math.round(gemessen ?? 0)}ms schonfrist-ton=${vorbehalt ? "ja" : "nein"}`,
      );
      if (vorbehalt && cb.onSelbstSenden) {
        schonGesendet = true;
        stoppeSelbstSenden();
        // 20.10.2026 (Vollständigkeits-Bremse): nichts zu prüfen — mit Mitschrift wäre `hatGesprochen()` wahr und dieser Weg zu.
        cb.onSelbstSenden("stille");
      } else if (vorbehalt) {
        cb.onStop();
      } else {
        cb.onCancel("nachlauschen-zu", "timer");
      }
      return true;
    }
    if (!nachlauschStandGemeldet && offen !== null && offen >= fenster && gemessen === null) {
      nachlauschStandGemeldet = true;
      meldeSpur("ptt", `nachlauschen-bleibt grund=pegel-steht nach=${offen}ms`);
    }
    return false;
  };
  /** 22.10.2026: die Wortlos-Frist (wortlosZu) — einmal je Aufnahme; geht wie nach Stille hinaus, nie verworfen. */
  const pruefeWortlos = (): boolean => {
    if (wortlosEntschieden || leerAbgebrochen || schonGesendet || card.dataset.state !== "recording") return false;
    const offen = aufnahmeSeit === null ? null : Date.now() - aufnahmeSeit;
    if (
      !wortlosZu({
        vonSelbst: nachlausch !== "aus",
        liveLief: liveAntwortKam && nachhoer === null,
        offenSeitMs: offen,
        liveWort: liveWortGehoert,
        nachhoerText: nachhoerTextKam || (nachhoer !== null && letzterText.trim() !== ""),
      })
    ) {
      return false;
    }
    wortlosEntschieden = true;
    meldeSpur("ptt", `wortlos-zu nach=${offen ?? 0}ms nachhoeren=${nachhoer ? "ja" : "nein"}`);
    nimmHinweisZurueck(NACHLAUSCH_HINWEIS, nachlauschVorher);
    if (cb.onSelbstSenden) {
      schonGesendet = true;
      stoppeSelbstSenden();
      // Wie das Nachlausch-Fenster mit Ton in der Schonfrist: kein Wort, nichts für die Vollständigkeits-Bremse zu prüfen.
      cb.onSelbstSenden("stille");
    } else {
      cb.onStop();
    }
    return true;
  };
  /** iOS-Hinweis (ios-diktat-hinweis.ts): erledigt, und was der Speicher beim ersten Lesen sagte. */
  let iosHinweisFertig = false;
  let iosHinweisGezeigt: boolean | null = null;
  const tick = (): void => {
    if (pruefeVerdeckt()) return;
    const verstrichen = Date.now() - startedAt;
    // 25.09.2026: aus Versehen gedrückt und nichts gesagt → still abbrechen.
    if (
      !leerAbgebrochen &&
      leerAbbrechen({
        laeuftSeitMs: aufnahmeSeit === null ? null : Date.now() - aufnahmeSeit,
        gesprochen: hatGesprochen(),
        echterTon: cb.hatEchtenTon?.() ?? false,
      })
    ) {
      leerAbgebrochen = true;
      meldeMikroAnlass("ptt-leer-abbruch", `nach=${Math.round((Date.now() - (aufnahmeSeit ?? Date.now())) / 1000)}s`);
      cb.onCancel("leer-abbruch", "timer");
      return;
    }
    if (pruefeNachlauschen()) return;
    if (pruefeWortlos()) return;
    const sec = Math.floor(verstrichen / 1000);
    const mm = Math.floor(sec / 60);
    const ss = (sec % 60).toString().padStart(2, "0");
    timerEl.textContent = `${mm}:${ss}`;
    /*
     * Ticket 2618 (29.09.2026): Geht die Aufnahme in Teilen weiter, kommt
     * vor jeder Teilgrenze ein ruhiger Satz — kein Ton, kein Amber (wer
     * sie hört, hört auf zu reden).
     *
     * ⚠️ Prüfbefund 29.09.: bis zur Nachbesserung fiel die Warnung weg,
     *    sobald geteilt WERDEN konnte. Kam der neue Teil dann nicht
     *    zustande, endete die Aufnahme ohne Vorwarnung — Alberts Fall vom
     *    16.09. Jetzt zählt die Uhr DES TEILS (vom Rekorder): hat der neue
     *    30 s vor der Höchstdauer nicht begonnen, warnt die Karte wie
     *    bisher. Beginnt er, fängt die Uhr von vorn an.
     */
    const lage = cb.teilLage?.() ?? null;
    const alter = lage ? lage.seitMs : verstrichen;
    if (lage?.teilt && alter >= PTT_MAX_MS - PTT_TEIL_HINWEIS_VOR_MS && angekuendigt <= lage.nr) {
      angekuendigt = lage.nr + 1;
      zeigeHinweis(teilKommtSatz(lage.nr + 1));
    }
    // 30.10.2026: das erste lange Diktat im iOS-Browser bekommt einmal den Hinweis zum Bildschirm.
    // Der Speicher wird nur einmal gelesen (erst wenn das Diktat lang ist), nicht in jedem Takt.
    if (!iosHinweisFertig && hinweisSatz === null && verstrichen >= LANGES_DIKTAT_MS) {
      const ios = typeof navigator !== "undefined" && istIosWeb(navigator.userAgent, navigator.maxTouchPoints ?? 0);
      iosHinweisGezeigt ??= hinweisSchonGezeigt(browserSpeicher());
      if (
        sollIosHinweisZeigen({ ios, inApp: cb.inApp === true, dauerMs: verstrichen, gezeigt: iosHinweisGezeigt })
      ) {
        merkeHinweisGezeigt(browserSpeicher());
        zeigeHinweis(IOS_DIKTAT_HINWEIS);
      }
      if (!ios || cb.inApp === true || iosHinweisGezeigt || hinweisSatz === IOS_DIKTAT_HINWEIS) iosHinweisFertig = true;
    }
    if (!gewarnt && alter >= PTT_MAX_MS - PTT_WARNUNG_VOR_MS) {
      gewarnt = true;
      timerEl.classList.add("pttm-timer--warnung");
      if (lage?.teilt) zeigeHinweis(teilHaktSatz(lage.nr + 1));
      /*
       * ⚠️⚠️ 18.09.2026 — DIE WARNUNG WIRD HOERBAR.
       *
       * Martin: *„Könnten wir da einen Alert machen, der im Prinzip das
       * Limit definiert, damit das nicht wieder passiert, dass er dann ins
       * Leere spricht?"*
       *
       * Bis heute pulsierten hier nur die Timer-Ziffern in Amber. Das sieht,
       * wer hinschaut — Albert hielt das Telefon ans Ohr und sprach nach dem
       * Limit sechs Minuten weiter.
       *
       * ⚠️ Der Ton kommt DREISSIG SEKUNDEN vor Schluss, nicht danach. Ein
       *    Signal nach dem Ende sagt nur, was verloren ist; dieses hier gibt
       *    die Wahl, den Satz zu Ende zu bringen und neu zu starten.
       */
      playBeep("achtung");
    }
  };
  const timerId = window.setInterval(tick, 250);

  /**
   * Die Karte schließen — EINE Stelle, mehrfach aufrufbar (das Senden, das
   * Ausblenden und der Aufrufer können sie nacheinander zumachen).
   */
  let zu = false;
  const schliessen = (): void => {
    if (zu) return;
    zu = true;
    // 12.10.2026: eine geschlossene Karte sendet nichts mehr von selbst (vorher lief der Takt weiter; die App fing es ab).
    stoppeSelbstSenden();
    leitungAbmelden();
    stummAbmelden();
    stopLevels();
    meldeStilleEinmal();
    window.clearInterval(timerId);
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("visibilitychange", onSichtbarkeit);
    overlay.classList.add("is-closing");
    window.setTimeout(() => {
      overlay.remove();
    }, AUSBLEND_MS);
  };
  /** Ticket 2634: „Sende …“ ausblenden — nichts abbrechen, nichts neu senden. */
  const ausblenden = (): void => {
    schliessen();
    cb.onAusblenden?.();
  };
  /**
   * Ein Druck auf „Verwerfen“ oder den Hintergrund. Bei einem von selbst geöffneten Fenster (`nachFrage`) zählt ein Druck in
   * den ersten GEISTERKLICK_SCHUTZ_MS nicht (Geister-Klick aufs Chat-Feld darunter), und ein Tipp auf den Hintergrund nie.
   * Escape ist kein Druck: Es läuft an der Schutzfrist vorbei (eine Taste, kein Geisterklick).
   */
  const druckVerwerfen = (quelle: "knopf" | "hintergrund"): void => {
    const offenSeitMs = Date.now() - startedAt;
    // Ein Tipp NEBEN die Karte ist bei einem von selbst geöffneten Fenster nie ein Verwerfen: Martin 06:55 wollte die Frage bewusst
    // beantworten und tippte „irgendwo“ — das verwarf die Frage. Verwerfen geht über den Knopf „Verwerfen“ oder Escape.
    if (quelle === "hintergrund" && cb.nachFrage === true) {
      meldeSpur("ptt", `druck-ignoriert quelle=hintergrund nach=${offenSeitMs}ms schutz=nach-frage-hintergrund`);
      return;
    }
    if (!verwerfenDruckZaehlt({ vonSelbst: cb.nachFrage === true, offenSeitMs })) {
      meldeSpur("ptt", `druck-ignoriert quelle=${quelle} nach=${offenSeitMs}ms schutz=geisterklick`);
      return;
    }
    cb.onCancel(undefined, quelle);
  };
  /**
   * Escape und Klick neben die Karte: vor dem Senden verwerfen sie die
   * Aufnahme, in „Sende …“ gibt es nichts mehr zu verwerfen — dort blenden
   * sie nur aus.
   */
  const verwerfenOderAusblenden = (quelle: "hintergrund" | "escape"): void => {
    if (card.dataset.state === "sending") ausblenden();
    else if (quelle === "escape") cb.onCancel(undefined, "escape");
    else druckVerwerfen("hintergrund");
  };

  // ESC schließt
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      verwerfenOderAusblenden("escape");
    }
  };
  document.addEventListener("keydown", onKey);

  // Klick auf Backdrop verwirft (nur außerhalb der Card)
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) verwerfenOderAusblenden("hintergrund");
  });

  let locked = false;
  /** 21.10.2026: endet diese Aufnahme im Eingabefeld (setInsFeld)? */
  let insFeld = false;
  const syncLabel = (s: string): void => {
    stateLabel.textContent = s;
  };

  return {
    setInsFeld() {
      insFeld = true;
      hauptText.textContent = "Ins Feld";
      stopBtn.setAttribute("aria-label", "Aufnahme stoppen und ins Eingabefeld setzen");
      if (card.dataset.state === "sending") syncLabel("Ins Feld …");
    },
    setStatus(s) {
      card.dataset.state = s;
      // Ticket 2634: der Ausweg gehört zu „Sende …“ und zu nichts sonst.
      ausweg.hidden = s !== "sending";
      if (s === "starting") {
        syncLabel("Öffne Mikro …");
      } else if (s === "recording") {
        // Ticket 2643: ein vor dem Aufnahmebeginn gemeldeter stummer Anfang.
        if (stummAusstehend && !stummGesagt) {
          stummAusstehend = false;
          sageStumm();
        }
        aufnahmeLief = true;
        aufnahmeSeit ??= Date.now();
        syncLabel(locked ? "Freihand" : "Aufnahme");
        startLevels();
        starteSelbstSenden();
        /*
         * Ticket 2668: ging das Mikro von selbst auf, lauscht es jetzt kurz.
         * Die Zeile sagt es, ein kurzer Ton auch — für wen nicht hinschaut.
         */
        if ((cb.nachlauschMs ?? 0) > 0 && nachlausch === "aus") {
          nachlausch = "an";
          nachlauschVorher = hinweisSatz;
          zeigeHinweis(NACHLAUSCH_HINWEIS);
          viz.grundLerntAb(performance.now() + NACHLAUSCH_GRUND_AB_MS);
          meldeSpur("ptt", `nachlauschen-auf fenster=${cb.nachlauschMs}ms`);
          /*
           * 19.10.2026 (Bereit-Ton): nicht mehr beim Aufruf des Starts, sondern ERST wenn der Rekorder wirklich aufnimmt
           * (rec-bereit.ts) — vorher sagte der Ton „sprich“, und das Mikro lieferte noch nichts. Der Ton selbst blockiert
           * nichts (der Start ist längst durch). Gegen das Zurückkoppeln: die Schonfrist des Pegels beginnt in dem Augenblick,
           * in dem der Ton wirklich klingt (er steht nicht in der Sprach-Messung, TON_SCHONFRIST_MS), und der Ton ist leise
           * mit 15-ms-Flanken. Aus: kein Ton, keine Schonfrist, wie ein Fenster ohne Ton.
           */
          if (cb.bereitTon === false) {
            meldeSpur("ptt", "bereit-ton nein grund=aus");
          } else {
            // Wie bisher gilt die Schonfrist ab dem Öffnen (der Ton kommt gleich) — und neu ab dem Augenblick, in dem er klingt.
            viz.tonZaehltAb(performance.now() + TON_SCHONFRIST_MS);
            wennRecBereit(
              BEREIT_TON_WARTE_MS,
              (nachMs) => {
                // Das Fenster kann inzwischen zu sein (verworfen, gesendet): dann klingt nichts.
                if (zu || card.dataset.state !== "recording") {
                  meldeSpur("ptt", "bereit-ton nein grund=fenster-zu");
                  return;
                }
                viz.tonZaehltAb(performance.now() + TON_SCHONFRIST_MS);
                playBeep("bereit");
                meldeSpur("ptt", `bereit-ton ja nachMs=${nachMs}`);
              },
              () => meldeSpur("ptt", "bereit-ton nein grund=kein-sample"),
            );
          }
        }
      } else if (s === "sending") {
        stopLevels();
        stoppeSelbstSenden();
        meldeStilleEinmal();
        // Pre.923: Timer einfrieren, sobald die Aufnahme stoppt — er lief
        // sonst bis zum STT-Roundtrip weiter.
        window.clearInterval(timerId);
        syncLabel(insFeld ? "Ins Feld …" : "Sende …");
      }
    },
    setSpeaking(active) {
      card.classList.toggle("is-speaking", active);
    },
    setLiveText(text, art) {
      const t = (text || "").trim();
      /*
       * Ticket 2420: wie lange der Text schon RUHT. Das ist Martins
       * Sicherung fuers Ausloesewort — nach „jetzt senden" darf nichts mehr
       * nachkommen. Sie haengt am Text, nicht am Pegel: gemeint ist, dass
       * die Erkennung nichts mehr nachliefert.
       *
       * ⚠️ 24.09.2026 (Ticket 2202): Ein Hinweis ist kein Text. Er steht nur
       *    da, wo sonst die Mitschrift steht — für die Entscheidung
       *    „gesprochen?" und fürs Auslösewort zählt er nicht. Sonst schickte
       *    die Stille-Schwelle eine native Aufnahme ab, bevor ein Wort fiel.
       */
      /*
       * Ticket 2672: Hört der Server nach, gehören Anzeige und Entscheidung
       * dem Nachgehörten. Eine Mitschrift, die wieder anläuft, darf dann
       * nicht dazwischenschreiben — zwei Quellen für denselben Text.
       */
      /*
       * Prüfauflage 22.10. (Wortlos-Frist): jede Antwort der Mitschrift belegt, dass sie läuft — auch eine leere; ein Hinweis
       * (nativer Weg) nie. Ein Wort zählt auch, solange das Nachhören die Anzeige hat (sonst schlösse die Frist bei Rede).
       */
      if (!art?.hinweis) {
        liveAntwortKam = true;
        if (t !== "") liveWortGehoert = true;
      }
      if (nachhoer) return;
      const erkannt = art?.hinweis ? "" : t;
      // Ticket 2867: ohne Live-Text gibt es kein `<end>` — die Zeile sagt es ehrlich.
      if (erkannt !== "") merkeLive(endeMessung);
      if (art?.woerter) merkeWortzahl(endeMessung, art.woerter.length);
      // 03.10.2026: ohne Wörter mit Uhrzeit keine Fantasiewörter (ein Hinweis hat keine).
      alleWoerter = erkannt ? (art?.alleWoerter ?? []) : [];
      if (erkannt !== letzterText) {
        letzterText = erkannt;
        textSeit = performance.now();
        // 05.10.2026: der Ton davor bekam sein Wort (ende-token.ts, ohneWortMaxMs).
        if (erkannt !== "") merkeTextNeu(endeMessung);
        zeigeJev();
      }
      // Auch bei gleichem Text: ein Wort wird oft erst später bestätigt,
      // und mit ihm kommen seine Zeitmarken (26.09.2026 spät).
      textVerlauf.merke(erkannt, textSeit, art?.woerter);
      zeigeMitschrift(t, art?.hinweis ? "hinweis" : "live");
    },
    setEndeToken(e) {
      // Ticket 2867: nur merken — die Stille ist die der Karte in diesem Moment.
      merkeEnde(endeMessung, e, stilleJetzt());
    },
    autoStand() {
      if (!jevMoeglich() || Number(cb.selbstSenden?.()?.stilleMs) !== STILLE_AUTO_MS) return "";
      const lage = sendeLage();
      // Rein lesend: dieselbe Lage wie im Takt, aber nichts wird gefragt oder gesendet (`rauschJetzt` merkt nur).
      const text = urteilsText(lage.text, rauschJetzt(lage.text));
      const u = autoUrteil;
      const gilt = u !== null && text !== "" && u.text === text;
      const jev = gilt && u.fertig !== null && Number.isFinite(u.fertig) ? u.fertig.toFixed(2) : "-";
      const rest = autoRestMs(autoLageVon(lage, text));
      return `stillMs=${Math.round(Math.max(0, lage.stillMs))} jev=${jev} satz=${text ? satzEnde(text) : "-"} autoRestMs=${rest ?? "-"}`;
    },
    ruheStand() {
      const ruht = liveTextRuhtMs();
      if (ruht === undefined || !Number.isFinite(ruht)) return "";
      const still = stilleJetzt();
      const sperre = Number.isFinite(still) ? Math.round(Math.max(0, ruht - Math.max(0, still))) : "-";
      // 20.10.2026 (Prüfauflage): ging es über den Notausgang (Dauerlärm), steht es eigens da — Fehlauslösungen sind zählbar.
      const notausgang = notausgangMs === null ? "" : ` notausgang=ja dauerLautMs=${notausgangMs}`;
      return `textRuhtMs=${Math.round(Math.max(0, ruht))} pegelSperreMs=${sperre}${notausgang}`;
    },
    autoZahlen() {
      const lage = sendeLage();
      // Rein lesend: dieselbe Lage wie im Takt (`rauschJetzt` merkt nur).
      const text = urteilsText(lage.text, rauschJetzt(lage.text));
      const u = autoUrteil;
      const gilt = u !== null && text !== "" && u.text === text;
      return {
        jev: gilt && u.fertig !== null && Number.isFinite(u.fertig) ? Math.round(u.fertig * 100) / 100 : null,
        satz: text ? satzEnde(text) : "-",
        stillMs: Number.isFinite(lage.stillMs) ? Math.round(Math.max(0, lage.stillMs)) : null,
      };
    },
    nachgehoert(p) {
      /*
       * Ticket 2668 (27.09.2026, Fall 07:58:52): das Nachgehörte zählt nur
       * mit Beleg am Pegel — es machte 18 Zeichen aus der Stille. Misst der
       * Pegel und kennt keinen anhaltenden Ton, gilt die Probe nicht: kein
       * Text, keine Anzeige, kein „gesprochen". Misst er nicht (Stillstand),
       * bleibt das Nachhören der Rückfall (siehe nachlauschenZu). Ein Wort
       * der Live-Mitschrift zählt immer.
       * Ticket 2697 (28.09.2026): nicht mehr nur im Nachlausch-Fenster. Seit
       * die Stille beim Nachhören am Pegel hängt (`stilleJetzt`), machte ein
       * erfundener Schnipsel sonst aus einer stummen Aufnahme „gesprochen",
       * und die Stille schickte sie ab.
       */
      if (!tonBelegt && pegelGemessenMs() !== null) return;
      const erstes = nachhoer === null;
      // 03.10.2026: das Nachgehörte hat keine Wörter mit Uhrzeit — keine Fantasiewörter.
      alleWoerter = [];
      nachhoer = { stilleMs: Math.max(0, p.stilleMs), tonDavor: p.tonDavor !== false };
      if (p.gesprochen) nachhoerHoerte = true;
      if (p.text !== null) {
        const t = p.text.trim();
        // Prüfauflage 22.10.: gemerkt — nach mitschriftZurueck zählt der frühere Nachhör-Text für die Wortlos-Frist weiter.
        if (t !== "") nachhoerTextKam = true;
        if (t !== letzterText) {
          letzterText = t;
          textSeit = performance.now();
          zeigeJev();
        }
        zeigeMitschrift(t, "nachgereicht");
      }
      if (erstes && card.dataset.state === "recording") {
        nachhoerVorher = hinweisSatz;
        nachhoerSatz = nachhoerHinweis();
        zeigeHinweis(nachhoerSatz);
      }
    },
    // Ob er gezeigt wird, entscheidet die Anzeige (eigene Messung geht vor; je Messung ein neuer Vorrat).
    pegelNachgereicht: (db) => viz.nachgereicht(db),
    pegelTot: () => pegelAufgegeben,
    sprichtSeitMs: () => (stimmeSeit === null ? null : performance.now() - stimmeSeit),
    mitschriftZurueck() {
      if (!nachhoer) return;
      nachhoer = null;
      // Die Ruhe des Texts zählt ab jetzt neu — die nächste Mitschrift setzt ihn.
      textSeit = performance.now();
      textVerlauf = neuerTextVerlauf();
      if (nachhoerSatz !== null) nimmHinweisZurueck(nachhoerSatz, nachhoerVorher);
      nachhoerSatz = null;
    },
    stummBisher: () => stummGesagt && !stummWiderlegt,
    nachlauschOhneSprache: () => nachlausch !== "aus" && !hatGesprochen(),
    teilBegonnen(nr) {
      // Ticket 2618: der neue Teil hat seine eigene Uhr — eine Warnung vor
      // dem Ende des vorigen gilt nicht mehr, vor seinem eigenen kommt sie
      // wieder.
      angekuendigt = Math.max(angekuendigt, nr);
      gewarnt = false;
      timerEl.classList.remove("pttm-timer--warnung");
      zeigeHinweis(teilLaeuftSatz(nr));
    },
    zeigeAusfall(satz, art) {
      zeigeHinweis(satz);
      if (art !== "ausfall") return;
      card.classList.add("pttm-ausfall");
      hinweisEl?.classList.add("pttm-anlass--ausfall");
      hinweisEl?.setAttribute("role", "alert");
    },
    setLocked(active) {
      locked = active;
      card.classList.toggle("is-locked", active);
      if (card.dataset.state === "recording") {
        syncLabel(active ? "Freihand" : "Aufnahme");
      }
    },
    close: schliessen,
  };
}

/** Ticket 2634: was unter „Ausblenden“ steht — wohin die Antwort geht. */
export const AUSBLENDEN_HINWEIS = "Die Antwort kommt im Verlauf.";

/** Ticket 2634: die Anzeige hängt, und niemand hört nach. */
const HAENGT_HINWEIS = "Die Anzeige zuckt nicht – die Aufnahme läuft. Zum Absenden bitte selbst tippen.";

/**
 * Der Anfang des Satzes, wenn die Anzeige hängt, das Nachhören aber mithört (`nachhoerHinweis`).
 *
 * 17.10.2026 (Martin): „Live-Anzeige hängt“ verstand er nicht — Laienwort „zuckt nicht“, ohne „Live“/„Pegel“.
 * Stimmt auch, wenn die Balken nachgereicht wieder schlagen oder gar kein Pegel ankommt.
 */
const HAENGT_MITHOEREN_ANFANG = "Die Anzeige zuckt nicht – ich höre trotzdem zu";

/**
 * Die Kurzfassung für die ruhige Statuszeile (01.10.2026, Martin: der lange Satz
 * stand in zwei Zeilen über der Mitschrift). Nur für die beiden Sätze „Die Anzeige zuckt nicht
 * …“ (17.10.2026: ohne „hängt“/„Live-Anzeige“, Martin verstand es nicht); alle anderen Hinweise (stummes Mikro, Verwerfen, Teile …) bleiben, wie
 * sie sind — dort steht, was jetzt zu tun ist, und das wird nicht gekürzt. Für
 * Vorleser steht weiter der ganze Satz da (`zeigeHinweis`).
 */
export function hinweisKurz(satz: string): string | null {
  if (satz.startsWith(HAENGT_MITHOEREN_ANFANG)) return "Anzeige zuckt nicht – ich höre trotzdem zu";
  if (satz === HAENGT_HINWEIS) return "Anzeige zuckt nicht – Aufnahme läuft. Selbst absenden.";
  return null;
}

/** Ticket 2668: das Mikro ging von selbst auf und lauscht kurz. */
export const NACHLAUSCH_HINWEIS = "Ich höre noch kurz zu …";

/**
 * Ticket 2643 (26.09.2026 abends): die Quelle liefert nur Stille — was
 * jetzt hilft. In der App liegt das Recht in den iPhone-Einstellungen, im
 * Browser bei der Seite.
 */
export function stummHinweis(inApp: boolean): string {
  // 29.09.2026: EINE Stelle für den Satz (mikro-recht.ts). Solange das
  // Recht nicht gelesen ist, behauptet er keine Ursache.
  return stummHinweisFuer(inApp, "unbekannt");
}

/** Ticket 2674: „Verwerfen" ist gesagt und wartet auf seine Ruhe. */
export const VERWERFEN_HINWEIS = "„Verwerfen“ gehört – gleich ist es weg. Sprich weiter, um es zu behalten.";

function textSpan(t: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.textContent = t;
  return s;
}

/**
 * Was der Auto-Mikro-Knopf je Stufe sagt. (23.09.2026, Ticket 2403)
 *
 * ⚠️ Der Titel sagt, was es TUT, nicht wie es heißt. „Auto-Mikro" allein
 *    verspricht Dauer-Zuhören — jede Stufe nennt ihren Anlass.
 */
const AUTO_MIKRO_TEXT = {
  aus: {
    label: "Auto-Mikro aus — tippen: nach einer Frage",
    titel:
      "Aus: das Mikro geht nur auf Knopfdruck auf — tippen, damit es nach einer Frage von selbst aufgeht",
    hinweis: "Auto-Mikro aus: das Mikro geht nur auf Knopfdruck auf.",
  },
  frage: {
    label: "Auto-Mikro nach einer Frage — tippen: Jev entscheidet",
    titel: "Nach einer Frage geht das Mikro von selbst auf — tippen: Jev entscheidet",
    hinweis: "Auto-Mikro: geht nach einer Frage von selbst auf.",
  },
  // Ticket 3048: ersetzt „nach jeder Antwort“ — derselbe Satz wie in den Einstellungen.
  jev: {
    label: "Auto-Mikro: Jev entscheidet — tippen zum Ausschalten",
    titel: "Jev entscheidet, ob das Mikro nach der Antwort aufgeht — tippen zum Ausschalten",
    hinweis: "Jev entscheidet: nach einer Frage geht das Mikro auf; ist Jev sicher, dass das Gespräch zu Ende ist, bleibt es zu — sonst geht es auf wie bisher.",
  },
} as const;

/** Lautsprecher mit Wellen + Durchstrich (Sichtbarkeit steuert CSS). */
/**
 * Mikrofon mit einem kleinen Bogen — „es geht von selbst auf".
 *
 * Bewusst KEIN zweites Lautsprecher-Symbol: daneben steht das Stummschalten,
 * und zwei aehnliche Zeichen nebeneinander sind schlechter als eines.
 */
function autoMikroIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const korpus = document.createElementNS(SVG_NS, "path");
  korpus.setAttribute(
    "d",
    "M12 13.5a2.5 2.5 0 0 0 2.5-2.5V6a2.5 2.5 0 1 0-5 0v5a2.5 2.5 0 0 0 2.5 2.5z",
  );
  korpus.setAttribute("fill", "currentColor");
  svg.appendChild(korpus);
  const buegel = document.createElementNS(SVG_NS, "path");
  buegel.setAttribute("class", "pttm-auto-buegel");
  buegel.setAttribute("d", "M7 11a5 5 0 0 0 10 0M12 16.5V19");
  buegel.setAttribute("fill", "none");
  buegel.setAttribute("stroke", "currentColor");
  buegel.setAttribute("stroke-width", "1.8");
  buegel.setAttribute("stroke-linecap", "round");
  svg.appendChild(buegel);
  return svg;
}

function speakerIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const body = document.createElementNS(SVG_NS, "path");
  body.setAttribute("d", "M11 5 6.5 9H3.5v6h3L11 19z");
  body.setAttribute("fill", "currentColor");
  svg.appendChild(body);
  const wave = document.createElementNS(SVG_NS, "path");
  wave.setAttribute("class", "pttm-mute-wave");
  wave.setAttribute(
    "d",
    "M14.5 8.7a4.6 4.6 0 0 1 0 6.6M17 6.2a8 8 0 0 1 0 11.6",
  );
  wave.setAttribute("stroke", "currentColor");
  wave.setAttribute("stroke-width", "2");
  wave.setAttribute("stroke-linecap", "round");
  wave.setAttribute("fill", "none");
  svg.appendChild(wave);
  const slash = document.createElementNS(SVG_NS, "path");
  slash.setAttribute("class", "pttm-mute-slash");
  slash.setAttribute("d", "M4.5 4.5l15 15");
  slash.setAttribute("stroke", "currentColor");
  slash.setAttribute("stroke-width", "2.4");
  slash.setAttribute("stroke-linecap", "round");
  slash.setAttribute("fill", "none");
  svg.appendChild(slash);
  return svg;
}

function svgIcon(d: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", d);
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.2");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("fill", "none");
  svg.appendChild(path);
  return svg;
}
